import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback, useImperativeHandle } from "react";
import { Download, Eye } from "lucide-react";
import html2pdf from "html2pdf.js";
import { PDFDocument } from "pdf-lib";
import { fetchMechanicsData } from "../services/mechanicsService";
import QuoteProgressService from "../services/quoteProgressService";
import { getSystemConfig,saveContractNotes } from "../services/systemConfigService";
import MobileService from "../services/mobileService";
import { useAuth } from "../context/AuthContext";
import { getRentalProfiling } from "../services/rentalProfilingService";
import {
  getBankDetails,
  updateBankDetails,
} from "../services/bankDetailsService";
import BankDetailsPopup from "./BankDetailsPopup";
import bankValidationService from "../services/bankValidationService";


const FibreContractForm = forwardRef(({ selectedConnectionId, isViewOnly = false }, ref) => {
  const [contractData, setContractData] = useState(null);
  const [managerName, setManagerName] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingAdobePDF, setIsGeneratingAdobePDF] = useState(false);
  const [crmIntegration, setCrmIntegration] = useState(0);
  const pdfRef = useRef();
  const leaseIdRef = useRef(0);
  const [services, setServices] = useState([]);
  const [userServices, setUserServices] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [specialConditions, setSpecialConditions] = useState("");
  const [cashback, setCashback] = useState("");
  const [hasRentalData, setHasRentalData] = useState(false);
  const [rentalData, setRentalData] = useState(null);
  const [showBankDetailsPopup, setShowBankDetailsPopup] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const { currentUser } = useAuth();
  const [userEmail, setUserEmail] = useState(null);
  const [digitalServices, setDigitalServices] = useState(null);
 // console.log("digitalServices",digitalServices);

  useEffect(() => {
    if (currentUser?.data?.email) {
      setUserEmail(currentUser.data.email);
    }
  }, [currentUser]);

  const customerRequirements = [
    {
      key: "pdqLine",
      text: "I currently have a PDQ line connected to Bank/Merchant Services",
    },
    {
      key: "alarmSystem",
      text: "I currently have a Redcare/similar alarm system connected",
    },
    {
      key: "smallBusiness",
      text: "I am aware, that by signing this contract as a small business of fewer than 10 employees that I waive my rights to the OfCom regulation of a maximum 24 month contract term.",
    },
    {
      key: "switchingDelay",
      text: "I am aware that there may be a delay in switching to your chosen carrier after installation has taken place, and that calls during this period may be routed through a different call carrier.",
    },
    {
      key: "summaryWaiver",
      text: "I am aware, that by signing this contract as a small business of fewer than 10 employees that I waive my right to be presented with a simple 1 page summary document in accordance with the EECC OfCom regulations",
    },
    {
      key: "terminationCharges",
      text: "I understand that any payment towards termination charges of an old supplier must have an invoice from the supplier along with a matching invoice from Fibre Telecoms UK Ltd",
    },
    {
      key: "settlementAgreement",
      text: "We acknowledge that Fibre Telecoms (UK) Ltd will only settle existing contracts to the amount stated in the order form, presented within 6 months and with a max 90-day payment term.",
    },
    {
      key: "leasingFunder",
      text: "I understand that my phone system rental payments will be made to a 3rd party leasing funder.",
    },
    {
      key: "specialConditions",
      text: "I understand that any 'Special Conditions' agreed by Fibre Telecoms (UK) Ltd representative may not be valid unless stated on the Order Form and initialled.",
    },
    {
      key: "cashback",
      text: "I understand that only the cash back for buyout charges agreed & listed on the Order form will be paid by Fibre Telecoms (UK) Ltd, and they are not liable for additional fees.",
    },
    {
      key: "networkUpgrade",
      text: "I understand that any upgrade and replication of network settlement is conditional on passing relevant credit checks.",
    },
    {
      key: "dataSharing",
      text: "I understand that Fibre Telecoms (UK) Ltd may share my data with 3rd party finance companies, in accordance with their Privacy Policy at www.fibretelecomsuk.co.uk",
    },
    {
      key: "rentalAgreement",
      text: "I agree that rentals due under the Rental Agreement have been calculated based on the full purchase price of the Equipment and must be paid in full without deductions or offsets.",
    },
    {
      key: "warranty",
      text: "I fully understand that Fibre Telecoms (UK) Ltd will remain solely responsible for warranty or service provision in a separate agreement. If the Supplier stops providing warranty, we must still comply with all Rental Agreement obligations.",
    },
    {
      key: "paymentTerms",
      text: "I understand that Fibre Telecoms (UK) Ltd will only pay sums agreed on the Order Form, and payments can take up to 90 days from receiving an invoice.",
    },
    {
      key: "earlyTermination",
      text: "I understand that Fibre reserves the right to challenge early termination charges with previous suppliers prior to payment.",
    },
    {
      key: "introBroadband",
      text: "I understand the terms of the introductory broadband and network settlement contributions.",
    },
  ];
  const documentsListLeft = [
    { key: "warrantyAgreement", label: "WARRANTY AGREEMENT" },
    { key: "networkServiceAgreement", label: "NETWORK SERVICE AGREEMENT" },
    { key: "lineRentalAgreement", label: "LINE RENTAL AGREEMENT" },
    { key: "mobileAirtimeAgreement", label: "MOBILE AIRTIME AGREEMENT" },
  ];

  const documentsListRight = [
    { key: "quotation", label: "QUOTATION - Inc. tariff" },
    { key: "rentalDocument", label: "RENTAL DOCUMENT" },
    { key: "thisForm", label: "THIS FORM" },
    { key: "orderForm", label: "ORDER FORM" },
  ];
  const mobileRequirements = [
    {
      key: "minTerm",
      text: "I understand that the minimum term for my mobile contract is 24 months.",
    },
    {
      key: "reImburse",
      text: "I understand that Fibre will only re-imburse buy outs for the allotted figure on the Order form.",
    },
    {
      key: "migrate",
      text: "I agree to help Fibre migrate my mobile phone numbers to their prefferred supplier by either getting the PAC	codes for them, or facilitating Fibre to get the PAC codes by providing Fibre with account information as requested.",
    },
    {
      key: "lineRental",
      text: "I understand that the contract with Fibre will commence whether I have provided PAC codes or not and that I am liable for line rental payments from the commencement date of this agreement.",
    },
    {
      key: "contractstartDate",
      text: "I understand that the start date of the Mobile contract will be as close to the stated date as possible.Fibre will endeavour to connect lines as early as possible, but due to influences outside of our control this could be delayed by your current supplier.",
    },
    {
      key: "unlockHandsets",
      text: "I understand that Fibre are not obliged to unlock handsets that are locked to alternative networks but may provide assistance in this matter.",
    },
    {
      key: "estimatedSavings",
      text: "I understand that the estimated savings will not start until I have had my existing and new numbers connected through Fibre and do not include international usage.",
    },
    {
      key: "upgradeHandsets",
      text: "I understand that I will be able to upgrade my handsets every two years.",
    },
    {
      key: "insurance",
      text: "I undertand that Fibre DO NOT provide insurance on the mobile handsets and it is my responsibility to insure them if I wish to.",
    },
    {
      key: "savings",
      text: "I understand that the saving is only offered if Fibre are successful in connecting us to Vodafone or o2 and that should it be neccessary to connect to any other network, then the savings will be less.",
    },

    {
      key: "package",
      text: "I understand that the quoted mobile savings are for a package equivalent to that already being provided	 and that additional talk time, texts, data and bolt-ons will be charged at Fibre's standard rate.",
    },
  ];
  const normalizeToDDMMYYYY = (value) => {
    if (!value) return "";

    // Handle YYYY-MM-DD or YYYY/MM/DD
    if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(value)) {
      const [y, m, d] = value.split(/[-/]/);
      return `${d}/${m}/${y}`;
    }

    // Already DD/MM/YYYY or partial typing
    return formatDateDDMMYYYY(value);
  };

  const formatDateDDMMYYYY = (value) => {
    // Remove anything that's not a digit
    const digits = value.replace(/\D/g, "").slice(0, 8);

    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);

    if (digits.length <= 2) return day;
    if (digits.length <= 4) return `${day}/${month}`;
    return `${day}/${month}/${year}`;
  };

  // Build default state for mobileRequirements
  const defaultMobileChecks = mobileRequirements.reduce((acc, item) => {
    acc[item.key] = true; // ✅ checked by default
    return acc;
  }, {});
  const [isPdfMode, setIsPdfMode] = useState(false);

  const [formData, setFormData] = useState({
    managerName: "",
    companyName: "",
    fullAddress: "",
    telephone: "",
    directorsEmail: "",
    billingEmail: "",
    agreementTerm: "7 YEARS",
    tariff: "FIBRE SIP BUNDLE",
    date: "",
    twentyFourHourService: "No",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    addressLine4: "",
    postcode: "",
    position: "",

    // Services
    callswitchReporting: 2,
    callswitchRecording: 0,
    softphoneMobile: 2,
    crmIntegration: 2,

    // Up front costs
    upfrontCost: 4384.16,
    networkSettlement: 220.0,
    monthlyRental: 86.27,

    // Checkboxes
    warrantyAgreement: true,
    networkServiceAgreement: true,
    lineRentalAgreement: true,
    mobileAirtimeAgreement: false,
    pdqLine: false,
    alarmSystem: false,
    smallBusiness: true,
    switchingDelay: true,
    summaryWaiver: true,
    upgradeProviso: true,
    introductoryTerms: true,
    thisForm: true,
    mobileAirtime: false,
    orderForm: true,
    quotation: true,
    rentalDocument: true,

    pdqLine: false,
    alarmSystem: false,
    smallBusiness: true,
    switchingDelay: true,
    summaryWaiver: true,
    terminationCharges: true,
    settlementAgreement: true,
    leasingFunder: true,
    specialConditions: true,
    cashback: true,
    networkUpgrade: true,
    dataSharing: true,
    rentalAgreement: true,
    warranty: true,
    paymentTerms: true,
    earlyTermination: true,
    introBroadband: true,
    salesName: "",

    customerName: "",
    phoneNumber: "",
    serviceLocation: "",
    serviceType: "",
    fiberServices: {
      installation: false,
      maintenance: false,
      upgrade: false,
    },
    customerServices: {
      residential: false,
      business: false,
      enterprise: false,
    },
    currentRetailerName: "BT",
    currentRetailerAddress: "81 NEWGATE STREET\nLONDON\nEC1A 7AJ",
    currentRetailerEmail: "",
    cloaBuildingName: "",
    cloaStreetName: "",
    cloaTownCity: "",
    cloaCounty: "",
    cloaPostCode: "",
    cloaMBN: "",
    numbersToBePorted: "",
    cloaCompanyName: "",
    cloaBillingAddress: "",
    cloaCompanyTownCity: "",
    cloaCompanyCounty: "",
    cloaCompanyPostCode: "",
    cloaCompanyRegNo: "",
    cloaBillingAccountNo: "",
    cloaSignature: "",
    cloaPrintName: "",
    cloaJobTitle: "",
    cloaDate: "",
    cloaEmail: "",
    cloaAdditionalSite1: "",
    cloaAdditionalNumbers1: "",
    cloaAdditionalSite2: "",
    cloaAdditionalNumbers2: "",
    cloaAdditionalSite3: "",
    cloaAdditionalNumbers3: "",
    cloaAdditionalSite4: "",
    cloaAdditionalNumbers4: "",
    cloaAdditionalSite5: "",
    cloaAdditionalNumbers5: "",
    newRetailerName: "TELCOSWITCH",
    newRetailerAddress:
      "Gamma Telecom Limited\n27-35 James House,\nNewbury, Berkshire,\nRG14 1JL",
    newRetailerEmail: "gnp@gamma.co.uk",
    serviceDetails: [
      {
        service: "Amount of PSTN / ISDN lines services",
        quantity: "",
        rate: "69.99",
      },
      {
        service: "Introductory Monthly Broadband charges",
        quantity: "",
        rate: "45.99",
      },
      { service: "Amount of Fibre Audio", quantity: "", rate: "79.99" },
      { service: "Amount of Hosted SIP Licenses", quantity: "", rate: "35.99" },
      {
        service: "Amount of SIP licence maintenance",
        quantity: "",
        rate: "125.99",
      },
      {
        service: "Amount of Fibre Perpetual web listings",
        quantity: 1,
        rate: "10",
      },
    ],
    ...defaultMobileChecks,
  });
  
  const hasDigitalServices = 
  digitalServices?.licenceServices?.Wizzyl > 0 ||
  digitalServices?.licenceServices?.["6ELA"] > 0 ||
  (digitalServices?.axoSignalPackage && Object.keys(digitalServices.axoSignalPackage).length > 0);

  useEffect(() => {
    const getMechanicsUcmIp = async () => {
      try {
        const response = await fetchMechanicsData();

        setMechanics(response.data?.services);
      } catch (error) {
        console.error("Failed to fetch UCM IP PBX:", error);
      }
    };

    getMechanicsUcmIp();
  }, []);
  useEffect(() => {
    const getRentalDetails = async () => {
      try {
        const jsonData = await getRentalProfiling(selectedConnectionId);

        if (jsonData && jsonData.status === "success" && jsonData.data) {
          setRentalData(jsonData.data);

          setHasRentalData(true);
        } else {
          setHasRentalData(false);
          setRentalData(null);
        }
      } catch (error) {
        console.error("Failed to fetch rental data:", error);
        setHasRentalData(false);
        setRentalData(null);
      }
    };

    if (selectedConnectionId) {
      getRentalDetails();
    }
  }, [selectedConnectionId]);

  const settlementsTotal =
    parseInt(rentalData?.calls_termination || 0, 10) +
    parseInt(rentalData?.lines_termination || 0, 10) +
    parseInt(rentalData?.current_lease_buy_outs || 0, 10);

   const DATA_PLAN_PRICES = {
    unlimited: 25,
    "40 gb": 21,
    "20 gb": 18,
    "10 gb": 15,
  };
  const DATA_ALLOWANCE_DISPLAY_LABELS = {
    unlimited: "Unlimited",
  };

  const getMobileSimDisplayAllowance = (allowance) => {
    const key = (allowance || "").toLowerCase();
    return DATA_ALLOWANCE_DISPLAY_LABELS[key] || allowance || "";
  };

  const mechanicsNameMap = {
    "Amount of PSTN / ISDN lines services": "ANALOGUE LINE",
    "Introductory Monthly Broadband charges": "SOGEA",
    "Amount of Fibre Audio": "FIBRE SAFE",
    "Amount of Hosted SIP Licenses": "SIP LICENSE",
    "Amount of SIP licence maintenance": "SIP MAINTENANCE",
    "Amount of Fibre Perpetual web listings": "FIBRE LISTINGS",
    "Fixed Mobile/Fibre Telephone/Advanced Email P...": "MOBILE EMAIL",
  };
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServiceChange = (category, service, checked) => {
    setFormData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [service]: checked,
      },
    }));
  };

  const handleServiceDetailChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      serviceDetails: prev.serviceDetails.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const calculateTotal = () => {
    return formData.serviceDetails
      .reduce((total, item) => {
        const quantity = parseInt(item.quantity) || 0;
        const rate = parseFloat(item.rate) || 0;
        return total + rate;
      }, 0)
      .toFixed(2);
  };

  const [hardwareItems, setHardwareItems] = useState([
    {
      id: 1,
      name: "Bell / Tannoy",
      quantity: 0,
      category: "Audio",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/bell.jpg",
    },
    {
      id: 2,
      name: "Mobile App",
      quantity: 0,
      category: "Software",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/mobileapp.jpg",
    },
    {
      id: 3,
      name: "Mobile App Addon",
      quantity: 0,
      category: "Software",
      icon: "📱",
    },
    {
      id: 4,
      name: "T54W",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/t54w.png",
    },
    {
      id: 5,
      name: "YEALINK T88",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/Yealink_new.jpg",
    },
     {
      id: 6,
      name: "YEALINK T85",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/yealinkt85.jpg",
    },
     {
      id: 7,
      name: "YEALINK T87",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/T87W.jpg",
    },
    {
      id: 8,
      name: "T58W",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/t58w.jpg",
    },
    {
      id: 9,
      name: "Yealink W73H DECT",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/ipdect.jpg",
    },
    {
      id: 10,
      name: "IP DECT Base",
      quantity: 0,
      category: "Phone",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/ipdect.jpg",
    },
    { id: 11, name: "Grandstream 3470", quantity: 0, category: "Phone", icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/Grandstream_3470.jpg" },
    { id: 12, name: "Grandstream 3480", quantity: 0, category: "Phone", icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/3480.jpg" },
    { id: 13, name: "Cisco 9871",       quantity: 0, category: "Phone", icon: "📞" },

    {
      id: 14,
      name: "Wi-Fi Booster",
      quantity: 0,
      category: "Network",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/wifibooster.jpg",
    },
    {
      id: 15,
      name: "Headsets",
      quantity: 0,
      category: "Audio",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/headset.jpg",
    },
    {
      id: 16,
      name: "Fibre TV/Wallboard",
      quantity: 0,
      category: "Display",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/fibretv.jpg",
    },
    {
      id: 17,
      name: "iPad",
      quantity: 0,
      category: "Tablet",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/ipad.png",
    },
    { id: 18, name: "Fibre Audio", quantity: 0, category: "Audio", icon: "🎧" },
    {
      id: 19,
      name: "Add On",
      quantity: 0,
      category: "Display",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/fibretv.jpg",
    },
    
  ]);
  const mobileAppUsers_noFoc = hardwareItems.reduce((total, item) => {
    if (item.name === "Mobile App" || item.name === "Mobile App Addon") {
      return total + item.quantity;
    }
    return total;
  }, 0);
  const mobileAppUsers = hardwareItems.reduce((total, item) => {
  if (item.name === "Mobile App" || item.name === "Mobile App Addon") {
    return total + (item.quantity || 0) + (item.focQuantity || 0);
  }
  return total;
}, 0);

  const [additionalHardware, setAdditionalHardware] = useState([
    {
      id: 18,
      name: "Fibre",
      quantity: 1,
      category: "Music Module",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/music.svg",
    },
    {
      id: 19,
      name: "Media Player",
      quantity: 1,
      category: "Music Module",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/module.svg",
    },
    {
      id: 20,
      name: "AA Voice Over",
      quantity: 1,
      category: "Music Module",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/music.svg",
    },
    {
      id: 21,
      name: "Site Link Licence",
      quantity: 0,
      category: "Site Link Licence",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/sitelink.png",
    },
    {
      id: 22,
      name: "Analogue Devices",
      quantity: 0,
      category: "Site Link Licence",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/sitelink.png",
    },
    {
      id: 23,
      name: "UPS",
      quantity: 1,
      category: "Protection",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/protection.svg",
    },
    {
      id: 24,
      name: "Surge protection",
      quantity: 1,
      category: "Protection",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/protection.svg",
    },
    {
      id: 25,
      name: "Pancode",
      quantity: 0,
      category: "Door entry",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/doorentry.png",
    },
    {
      id: 26,
      name: "1 Button Entry",
      quantity: 0,
      category: "Door entry",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/doorentry.png",
    },
    {
      id: 27,
      name: "UCM IP PBX",
      quantity: 1,
      category: "UCM IP PBX",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/ucmippbx.jpg",
    },
    {
      id: 28,
      name: "Voicemail ports",
      quantity: 0,
      category: "UCM IP PBX",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/ucmippbx.jpg",
    },
    {
      id: 29,
      name: "Draytek 2865LAC",
      quantity: 0,
      category: "Router",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/router.png",
    },
    {
      id: 30,
      name: "Netgear GS108LP",
      quantity: 0,
      category: "POE",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/poe.png",
    },
    {
      id: 31,
      name: "24 port POE",
      quantity: 0,
      category: "POE",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/poe.png",
    },
    {
      id: 32,
      name: "Cat 5E Cable (5m)",
      quantity: 0,
      category: "Cabling",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/cat5e.jpg",
    },
    {
      id: 33,
      name: "System Moves",
      quantity: 0,
      category: "System Moves",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/systemmoves.png",
    },
    {
      id: 34,
      name: "Cat 5",
      quantity: 0,
      category: "System Moves",
      icon: "https://quoting-tool.fibretelecomsuk.co.uk/images/systemmoves.png",
    },
    
  ]);
  const [lineRequirements, setLineRequirements] = useState({
    existingIsdn2Channels: 0,
    newConversionIsdn2Channel: 0,
    existingIsdn30Channels: 0,
    newIsdn30Channel: 0,
  });
  

  const [currentLines, setCurrentLines] = useState({
    hostedLifetimeLicenses: 0,
    analoguePstnLines: 0,
    adslFttcBroadband: 0,
    new4g5gBroadband: 0,
    fttpSogeaBroadband: 0,
  });
  const [dataBroadband, setDataBroadband] = useState({
    starlinkBroadband: 0,
    leasedLines: 0,
  });
  const [linesFoc, setLinesFoc] = useState({
    hostedLifetimeLicensesFocQty: 0,
    new4g5gBroadbandFocQty: 0,
    fttpSogeaBroadbandFocQty: 0,
    starlinkBroadbandFocQty: 0,
    leasedLinesFocQty: 0,
  });
  const [serviceCharges, setServiceCharges] = useState({
    broadband: 0,
    equipment_rentals: 0,
    maintenance: 0,
    media: 0,
    service_charge: 0,
    utilities: 0,
  });
  const [leaseCalculatorData, setLeaseCalculatorData] = useState({
    leaseId: 0,
    actualLeasePayment: 0,
    actualProfit: 0,
    adjustedRental: 0,
    currentCallCharges: 0,
    current_lease_rental: 0,
    current_maintenance: 0,
    current_mobile_charges: 0,
    current_service_charges: 0,
    current_total_cost: 0,
    discountLoad: 0,
    estimatedCallCharges: 0,
    estimated_mobile_charges: 0,
    leased_line: 0,
    it_service_charge: 0,
    it_services_total: 0,
    networkPayment: 0,
    networkSettlementContribution: 0,
    new_service_charges: 0,
    other_charges: 0,
    paymentProfile: "",
    salesRepCommission: 0,
    totalNewDigitalCost: 0,
    webHostingCost: 0,
    ai_receptionist_enabled: false,
    ai_receptionist_charge: 0,
  });
  
  const [telephoneLines, setTelephoneLines] = useState();
  // Page Footer Component
  // Page Footer Component
  const PageFooter = ({
    pageNumber,
    totalPages = 5,
    showInitials = true,
    sectionTitle = "INITIAL HERE",
  }) => {
    return (
      <div
        className="flex justify-between items-end border-t-2 border-black px-3 py-2 text-xs signature-section"
        data-section={`footer_page_${pageNumber}`}
      >
        {/* Left: Page Number */}
        <div className="font-semibold">
          PAGE {pageNumber} OF {totalPages}
        </div>

        {/* Right: Initials */}
        {showInitials && (
          <div className="flex items-end">
            <div className="flex flex-col items-start">
              {/* Label */}
              <div className="text-[10px] font-semibold mb-1">
                {sectionTitle} <span className="text-red-500">*</span>
              </div>

              {/* ✅ Initials Box - Same as working cost breakdown */}
              <div className="border border-black flex items-center justify-center">
                {"{{Int1_es_:signer1:initials}}"}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
   const [mobileData, setMobileData] = useState(null);

    const debounceRef = useRef(null);
    const latestNotesRef = useRef({ specialConditions: "", cashback: "" });
    const initialLoadDoneRef = useRef(false); // ← ADD THIS LINE

  const persistNotes = useCallback(async () => {
    if (!selectedConnectionId) return;
    try {
      await saveContractNotes(selectedConnectionId, latestNotesRef.current);
    } catch (err) {
      console.error("Failed to save contract notes:", err);
    }
  }, [selectedConnectionId]);

  useEffect(() => {
    if (isViewOnly) return;
    if (!initialLoadDoneRef.current) return;

    latestNotesRef.current = { specialConditions, cashback };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(persistNotes, 800);
    return () => clearTimeout(debounceRef.current);
  }, [specialConditions, cashback, persistNotes, isViewOnly]);

  useImperativeHandle(ref, () => ({
    flushContractNotes: () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      return persistNotes();
    },
  }));

  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        if (!selectedConnectionId) return;
        initialLoadDoneRef.current = false; 
        const { data } = await getSystemConfig(selectedConnectionId);
        
        if (data) {
          setContractData(data);
          const numbersToBePorted = data.telecom_details
            ? data.telecom_details
                .filter((item) => item.existing_tel_no) // Filter out empty values
                .map((item) => item.existing_tel_no)
                .join("\n")
            : "";

          // Update formData
          setFormData((prev) => ({
            ...prev,
            managerName: data.md_name || prev.managerName,
            companyName: data.connection_name || prev.companyName,
            companyEmail: data.director_email || prev.companyEmail,
            telephone: data.telephone || prev.telephone,
            cloaCompanyRegNo: data.company_reg || prev.company_reg,
            billingEmail: data.billing_email || prev.billingEmail,
            date: data.contract_date || prev.contract_date,
            salesName: data.sales_man,
            addressLine1: data.registered_address_line_1 || prev.addressLine1,
            addressLine2: data.registered_address_line_2 || prev.addressLine2,
            addressLine3: data.registered_address_line_3 || prev.addressLine3,
            addressLine4: data.registered_address_line_4 || prev.addressLine4,
            postcode: data.postcode || prev.postcode,
            position: data.position,
            fullAddress: [
              data.registered_address_line_1,
              data.registered_address_line_2,
              data.registered_address_line_3,
              data.postcode,
            ]
              .filter(Boolean)
              .join("\n"),
            numbersToBePorted: numbersToBePorted || prev.numbersToBePorted,
          }));

          // Merge hardwareItems
          if (Array.isArray(data.hardwareItems)) {
           setHardwareItems((prev) =>
              prev.map((item) => {
                const backendItem = data.hardwareItems.find((h) => h.name === item.name);
                return backendItem
                  ? { ...item, quantity: backendItem.quantity || 0, focQuantity: backendItem.focQuantity || 0 }
                  : item;
              }),
            );
          }

          // Merge additionalHardware safely
          const backendAdditional = data.additionalHardware
            ? Array.isArray(data.additionalHardware)
              ? data.additionalHardware
              : Object.values(data.additionalHardware)
            : [];

          setAdditionalHardware((prev) =>
            prev.map((item) => {
              const backendItem = backendAdditional.find(
                (h) => h.name === item.name,
              );
              return backendItem
                ? { ...item, quantity: backendItem.quantity }
                : item;
            }),
          );

          if (data.currentLines) {
            setCurrentLines({
              hostedLifetimeLicenses:
                data.currentLines.hostedLifetimeLicenses || 0,
              analoguePstnLines: data.currentLines.analoguePstnLines || 0,
              adslFttcBroadband: data.currentLines.adslFttcBroadband || 0,
              new4g5gBroadband: data.currentLines.new4g5gBroadband || 0,
              fttpSogeaBroadband: data.currentLines.fttpSogeaBroadband || 0,
            });
          }

          // Data Broadband
          if (data.dataBroadband) {
            setDataBroadband({
              starlinkBroadband: data.dataBroadband.starlinkBroadband || 0,
              leasedLines: data.dataBroadband.leasedLines || 0,
            });
          }

          if (data.lineRequirements) {
            setLineRequirements({
              existingIsdn2Channels:
                data.lineRequirements.existingIsdn2Channels,
              newConversionIsdn2Channel:
                data.lineRequirements.newConversionIsdn2Channel,
              existingIsdn30Channels:
                data.lineRequirements.existingIsdn30Channels,
              newIsdn30Channel: data.lineRequirements.newIsdn30Channel,
            });
          }

          // Service Charges
          if (data.serviceCharges) {
            setServiceCharges({
              broadband: data.serviceCharges.broadband || 0,
              equipment_rentals: data.serviceCharges.equipment_rentals || 0,
              maintenance: data.serviceCharges.maintenance || 0,
              media: data.serviceCharges.media || 0,
              service_charge: data.serviceCharges.service_charge || 0,
              utilities: data.serviceCharges.utilities || 0,
            });
          }
         


          if (data.leaseCalculatorData) {
           
            setLeaseCalculatorData({
              leaseId:data.leaseCalculatorData.id || 0,
              actualLeasePayment:
                data.leaseCalculatorData.actualLeasePayment || 0,
              actualProfit: data.leaseCalculatorData.actualProfit || 0,
              adjustedRental: data.leaseCalculatorData.adjustedRental || 0,
              currentCallCharges:
                data.leaseCalculatorData.currentCallCharges || 0,
              current_lease_rental:
                data.leaseCalculatorData.current_lease_rental || 0,
              current_maintenance:
                data.leaseCalculatorData.current_maintenance || 0,
              current_mobile_charges:
                data.leaseCalculatorData.current_mobile_charges || 0,
              current_service_charges:
                data.leaseCalculatorData.current_service_charges || 0,
              current_total_cost:
                data.leaseCalculatorData.current_total_cost || 0,
              discountLoad: data.leaseCalculatorData.discountLoad || 0,
              estimatedCallCharges:
                data.leaseCalculatorData.estimatedCallCharges || 0,
              estimated_mobile_charges:
                data.leaseCalculatorData.estimated_mobile_charges || 0,
              leased_line: data.leaseCalculatorData.leased_line || 0,
              it_service_charge: data.leaseCalculatorData.it_service_charge || 0, 
              it_services_total: data.itServicesTotal ?? 0,
              networkPayment: data.leaseCalculatorData.networkPayment || 0,
              networkSettlementContribution:
                data.leaseCalculatorData.networkSettlementContribution || 0,
              new_service_charges:
                data.leaseCalculatorData.new_service_charges || 0,
              other_charges: data.leaseCalculatorData.other_charges || 0,
              paymentProfile: data.leaseCalculatorData.paymentProfile || "",
              salesRepCommission:
                data.leaseCalculatorData.salesRepCommission || 0,
              totalNewDigitalCost: data.leaseCalculatorData.digital_services?.totalNewDigitalCost ?? 0,  // ✅ FIXED
              webHostingCost: data.leaseCalculatorData.digital_services?.webHostingCost ?? 0,            // ✅ FIXED
              ai_receptionist_enabled: Boolean(data.leaseCalculatorData.ai_receptionist_enabled),
              ai_receptionist_charge: data.leaseCalculatorData.ai_receptionist_enabled
                ? Number(data.leaseCalculatorData.ai_receptionist_charge) || 0
                : 0,
            });
          }

          if (data.linesFoc) {
            setLinesFoc({
              hostedLifetimeLicensesFocQty: data.linesFoc.hostedLifetimeLicensesFocQty || 0,
              new4g5gBroadbandFocQty: data.linesFoc.new4g5gBroadbandFocQty || 0,
              fttpSogeaBroadbandFocQty: data.linesFoc.fttpSogeaBroadbandFocQty || 0,
              starlinkBroadbandFocQty: data.linesFoc.starlinkBroadbandFocQty || 0,
              leasedLinesFocQty: data.linesFoc.leasedLinesFocQty || 0,
            });
          }
          if (data.leaseCalculatorData?.digital_services) {
            setDigitalServices(data.leaseCalculatorData.digital_services);
          }
          setSpecialConditions(data.special_conditions || "");
          setCashback(data.cashback != null ? String(data.cashback) : "");

          if (Array.isArray(data.telecom_details)) {
            setTelephoneLines(
              data.telecom_details.map((line) => ({
                existing_tel_no: line.existing_tel_no || "",
                quantity: line.quantity || 0,
                description: line.description || "",
                postcode: line.postcode || "",
                type: line.service_type || "",
              })),
            );
          } else {
            setTelephoneLines([]); // fallback so .map() won't break
          }
        }
      } catch (error) {
      console.error("Failed to load config:", error);
    } finally {
      initialLoadDoneRef.current = true;
    }
  };


    fetchConfigData();
  }, [selectedConnectionId]);

  // 🧮 Build the service details list from calculateAllCosts
  const calculateAllCosts = useMemo(() => {
  if (!mechanics || !currentLines || !dataBroadband || !lineRequirements || !hardwareItems) {
    return { totalMonthly: 0, serviceDetails: [] };
  }


  const getPrice = (serviceName) => {
    const service = mechanics.find((s) => s.name === serviceName);
   
    return parseFloat(service?.price || 0);
  };

  const roundToTwo = (num) =>
    parseFloat((Math.round(num * 100) / 100).toFixed(2));

  const getHwTotal = (name) => {
    const item = hardwareItems.find((i) => i.name === name);
    return (item?.quantity || 0) + (item?.focQuantity || 0);
  };

  const getHwBillableQty = (name) => {
    const item = hardwareItems.find((i) => i.name === name);
    return item?.quantity || 0;
  };

  const fibre_fail_safe_sum =
    currentLines.adslFttcBroadband +
    currentLines.new4g5gBroadband +
    currentLines.fttpSogeaBroadband;

// Inside calculateAllCosts, where `quantities` is built:
const quantities = {
  SOGEA: currentLines.fttpSogeaBroadband + (linesFoc.fttpSogeaBroadbandFocQty || 0),
  "FTTC BB": currentLines.adslFttcBroadband,
  "4G BB": currentLines.new4g5gBroadband + (linesFoc.new4g5gBroadbandFocQty || 0),
  STARLINK: dataBroadband.starlinkBroadband + (linesFoc.starlinkBroadbandFocQty || 0),
  "ANALOGUE LINE": currentLines.analoguePstnLines,
  "ISDN 2 CH": lineRequirements.existingIsdn2Channels,
  T58W: getHwTotal("T58W"),
  T57W: getHwTotal("T57W"),
  "YEALINK T88": getHwTotal("YEALINK T88"),
  "YEALINK T85": getHwTotal("YEALINK T85"),
  "YEALINK T87": getHwTotal("YEALINK T87"),
  "GRANDSTREAM 3470": getHwTotal("Grandstream 3470"),
  "GRANDSTREAM 3480": getHwTotal("Grandstream 3480"),
  "CISCO 9871": getHwTotal("Cisco 9871"),
  DECT: getHwTotal("Yealink W73H DECT"),
  "LICENSE MAINT": getHwTotal("Mobile App"),
  "SIP TRUNK BUNDLE": getHwTotal("Mobile App"),
  "LEASED LINE": dataBroadband.leasedLines + (linesFoc.leasedLinesFocQty || 0),
  "FIBRE FAILSAFE": fibre_fail_safe_sum,
  "FIBRE SAFE": 1,
  "FIBRE AUDIO": getHwTotal("Fibre Audio"),
  "FIBRE AI": 1,
};

// NEW — one entry per active mobile data plan, named by its actual allowance
// NEW — one entry per active mobile data plan, named by its display allowance
(mobileData?.data_plans || []).forEach((plan) => {
  const qty = plan?.count || 0;
  if (qty > 0) {
    const displayAllowance = getMobileSimDisplayAllowance(plan.data_allowance);
    const label = `MOBILE SIM ${displayAllowance}`.trim().toUpperCase();
    quantities[label] = qty;
  }
});
  // Billable quantities (used for rate calculation — excludes FOC)
  const billableQuantities = {
    // Broadband/lines — subtract FOC qty
    SOGEA: Math.max(0, currentLines.fttpSogeaBroadband - (linesFoc.fttpSogeaBroadbandFocQty || 0)),
    "FTTC BB": currentLines.adslFttcBroadband, // no FOC field exists for FTTC
    "4G BB": Math.max(0, currentLines.new4g5gBroadband - (linesFoc.new4g5gBroadbandFocQty || 0)),
    STARLINK: Math.max(0, dataBroadband.starlinkBroadband - (linesFoc.starlinkBroadbandFocQty || 0)),
    "ANALOGUE LINE": currentLines.analoguePstnLines,
    "ISDN 2 CH": lineRequirements.existingIsdn2Channels,
    // Hardware — exclude focQuantity
    T58W: getHwBillableQty("T58W"),
    T57W: getHwBillableQty("T57W"),
    "YEALINK T88": getHwBillableQty("YEALINK T88"),
    "YEALINK T85": getHwBillableQty("YEALINK T85"),
    "YEALINK T87": getHwBillableQty("YEALINK T87"),
    "GRANDSTREAM 3470": getHwBillableQty("Grandstream 3470"),
    "GRANDSTREAM 3480": getHwBillableQty("Grandstream 3480"),
    "CISCO 9871": getHwBillableQty("Cisco 9871"),
    DECT: getHwBillableQty("Yealink W73H DECT"),
    "LICENSE MAINT": getHwBillableQty("Mobile App"),
    "SIP TRUNK BUNDLE": getHwBillableQty("Mobile App"),
    "LEASED LINE": Math.max(0, dataBroadband.leasedLines - (linesFoc.leasedLinesFocQty || 0)),
    // Fixed services — no FOC concept
    "FIBRE FAILSAFE": fibre_fail_safe_sum,
    "FIBRE SAFE": 1,
    "FIBRE AUDIO": getHwBillableQty("Fibre Audio"),
    "FIBRE AI": 1,
  };

const mobileSimPriceByLabel = {};
  (mobileData?.data_plans || []).forEach((plan) => {
    const qty = plan?.count || 0;
    if (qty > 0) {
      const displayAllowance = getMobileSimDisplayAllowance(plan.data_allowance);
      const label = `MOBILE SIM ${displayAllowance}`.trim().toUpperCase();
      const priceKey = plan.data_allowance?.toLowerCase(); // real allowance, for correct price
      const price = DATA_PLAN_PRICES[priceKey] || 0;
      mobileSimPriceByLabel[label] = price;
    }
  });
//console.log("quantities",quantities);
const serviceDetails = Object.entries(quantities)
  .filter(([name, qty]) => qty > 0 || name === "FIBRE AUDIO")
  .map(([name, qty]) => {
    const isMobileSim = name.startsWith("MOBILE SIM");
    const isFlatAddon = name === "FIBRE AUDIO"; // billed as a flat fee, not qty × price

    const billableQty = isMobileSim ? qty : billableQuantities[name];
    const price = isMobileSim ? mobileSimPriceByLabel[name] || 0 : getPrice(name);

    const effectiveQty = isFlatAddon ? (qty || 1) : qty;

    const rate = isFlatAddon
      ? roundToTwo(effectiveQty > 1 ? effectiveQty + price : price) // flat fee, but qty>1 adds to price
      : roundToTwo(billableQty * price);       // normal per-unit pricing

    return {
      service: name,
      quantity: effectiveQty, // show at least 1 if it's the default addon
      rate,
    };
  });

  const totalMonthly = roundToTwo(
    serviceDetails.reduce((sum, s) => sum + s.rate, 0),
  );

  return { totalMonthly, serviceDetails };
}, [mechanics, currentLines, dataBroadband, lineRequirements, hardwareItems, linesFoc]);
//                                                                                  ↑ added
 
  // Mapping of your service names to mechanics names and quantity paths
  const serviceConfigMap = {
    "Amount of PSTN / ISDN lines services": {
      mechanicsName: "ANALOGUE LINE",
      quantityPath: "currentLines.analoguePstnLines",
    },
    "Introductory Monthly Broadband charges": {
      mechanicsName: "SOGEA",
      quantityPath: "serviceCharges.broadband",
    },
    "Amount of Fibre Audio": {
      mechanicsName: "FIBRE SAFE",
      quantityPath: "additionalHardware.Fibre",
    },
    "Amount of Hosted SIP Licenses": {
      mechanicsName: "SIP TRUNK BUNDLE",
      quantityPath: "currentLines.hostedLifetimeLicenses",
    },
    "Amount of SIP licence maintenance": {
      mechanicsName: "LICENSE MAINT",
      quantityPath: "currentLines.hostedLifetimeLicenses",
    },
  };
  

  const adslQty = currentLines.adslFttcBroadband || 0;
  const fttpQtyFoc = (currentLines.fttpSogeaBroadband || 0) + (linesFoc.fttpSogeaBroadbandFocQty || 0);
  const new4GQtyFoc = (currentLines.new4g5gBroadband || 0) + (linesFoc.new4g5gBroadbandFocQty || 0);
  const starlinkQtyFoc = (dataBroadband.starlinkBroadband || 0) + (linesFoc.starlinkBroadbandFocQty || 0);
  const leasedLineFoc = (dataBroadband.leasedLines || 0) + (linesFoc.leasedLinesFocQty || 0);
  const fttpQty = (currentLines.fttpSogeaBroadband || 0);
  const new4GQty = (currentLines.new4g5gBroadband || 0);
  const starlinkQty = (dataBroadband.starlinkBroadband || 0);
  const leasedLine = (dataBroadband.leasedLines || 0);
  const broadbandService = adslQty + fttpQtyFoc + starlinkQtyFoc + leasedLineFoc;

  useEffect(() => {
    if (!mechanics.length) return;

    setFormData((prev) => ({
      ...prev,
      serviceDetails: prev.serviceDetails.map((item) => {
        let quantity = 0;
        let rate = 0;

        if (item.service === "Introductory Monthly Broadband charges") {
          // Find each broadband mechanic
          const fttc = mechanics.find((m) => m.name === "FTTC BB");
          const sogea = mechanics.find((m) => m.name === "SOGEA");
          const fourG = mechanics.find((m) => m.name === "4G BB");
          const starlink = mechanics.find((m) => m.name === "STARLINK");

          // Calculate total rate
          rate =
            adslQty * (fttc ? parseFloat(fttc.price) : 0) +
            fttpQty * (sogea ? parseFloat(sogea.price) : 0) +
            new4GQty * (fourG ? parseFloat(fourG.price) : 0) +
            starlinkQty * (starlink ? parseFloat(starlink.price) : 0);

          // Optionally, sum of all quantities
          quantity = adslQty + fttpQty + new4GQty + starlinkQty;
        } else if (item.service === "Amount of Fibre Perpetual web listings") {
          quantity = 1;
          const fibreweb = mechanics.find((m) => m.name === "FIBRE AI");

          rate = fibreweb ? parseFloat(fibreweb.price) : 0;
        } else {
          const config = serviceConfigMap[item.service];

          if (!config) return item;
          const mechanic = mechanics.find(
            (m) => m.name === config.mechanicsName,
          );
          const price = mechanic ? parseFloat(mechanic.price) : 0;

          if (config.quantityPath.startsWith("additionalHardware")) {
            // e.g. "additionalHardware.Fibre"
            const [, hwName] = config.quantityPath.split(".");
            const hwItem = additionalHardware.find((h) => h.name === hwName);
            quantity = hwItem ? hwItem.quantity : 0;

            // For additional hardware: rate = just the price (not quantity * price)

            rate = price;
          } else {
            const pathParts = config.quantityPath.split(".");
            const key = pathParts[1];
            quantity = currentLines[key] || 0;

            rate = quantity * price;
          }
        }

        return { ...item, quantity, rate: rate.toFixed(2) };
      }),
    }));
  }, [mechanics, currentLines, dataBroadband]);

 

useEffect(() => {
    const getMobileDetails = async () => {
      try {
        const response =
          await MobileService.getMobileService(selectedConnectionId);

        setMobileData(response); // Send full raw data to child
      } catch (error) {
        console.error("Failed to fetch:", error);
      }
    };

    getMobileDetails();
  }, [selectedConnectionId]);

  const getDataCount = (allowance) => {
    return (
      mobileData?.data_plans?.find((plan) => plan.data_allowance === allowance)
        ?.count || 0
    );
  };
  const getStaffCount = (allowance) => {
    return (
      mobileData?.staff_levels?.find((plan) => plan.staff_level === allowance)
        ?.count || 0
    );
  };

  const broadbandServiceTotal = formData.serviceDetails.find(
    (s) => s.service === "Introductory Monthly Broadband charges",
  );

  useEffect(() => {
    if (selectedConnectionId) {
      checkAndHandleBankDetails();
    }
  }, [selectedConnectionId]);

  const newChargesTotal =
    (leaseCalculatorData?.adjustedRental || 0) +
    (leaseCalculatorData?.estimated_mobile_charges || 0) +
    (leaseCalculatorData?.estimatedCallCharges || 0) +
    (leaseCalculatorData?.new_service_charges || 0) +
    (leaseCalculatorData?.networkSettlementContribution || 0) +
    (leaseCalculatorData?.leased_line || 0) +
    (leaseCalculatorData?.it_service_charge || 0) +
    (leaseCalculatorData?.it_services_total || 0) +
    (leaseCalculatorData?.totalNewDigitalCost || 0) +
    (leaseCalculatorData?.ai_receptionist_charge || 0);

  const leaseId= leaseCalculatorData?.leaseId;
 
  // Utility function to format date to dd/mm/yyyy
  const formatToDDMMYYYY = (date) => {
    if (!date) return "";

    // Always create a Date object from string
    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return ""; // Invalid date safeguard

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${day}/${month}/${year}`;
  };

 
  const getHardwareQty = (name, fixedQty = null) => {
  if (fixedQty !== null) return fixedQty;
  const item = hardwareItems.find((h) => h.name === name);
  return (item?.quantity || 0) + (item?.focQuantity || 0); // ← include FOC in total
  };

  const getAdditionalHardwareQty = (name, fixedQty = null) => {
    if (fixedQty !== null) return fixedQty;
    const item = additionalHardware.find((h) => h.name === name);
    return (item?.quantity || 0) + (item?.focQuantity || 0); // ← include FOC in total
  };

  // Example data mapping
  const equipmentData = [
    {
      equipment: "SIP Bell",
      qty: getHardwareQty("Bell / Tannoy"),
      feature: "On Hold Media Player",
      featureQty: getAdditionalHardwareQty("Media Player"),
      service: "CallSwitch Mobile Client",
      serviceQty: getHardwareQty("Mobile App"),
    },
    {
      equipment: "Yealink T54W",
      qty: getHardwareQty("T54W"),
      feature: "Wi-Fi Access Point",
      featureQty: getHardwareQty("Wi-Fi Booster"),
      service: "Programming software",
      serviceQty: 1,
    },
    {
      equipment: "Yealink T57W",
      qty: getHardwareQty("T57W"),
      feature: "Cordless Headset",
      featureQty: getHardwareQty("Headsets"),
      service: "Call Logging",
      serviceQty: mobileAppUsers,
    },
    {
      equipment: "YEALINK T88",
      qty: getHardwareQty("YEALINK T88"),
      feature: "On Hold Marketing Recording",
      featureQty: getAdditionalHardwareQty("Fibre"),
      service: "Call Reporting",
      serviceQty: mobileAppUsers,
    },
     {
      equipment: "YEALINK T85",
      qty: getHardwareQty("YEALINK T85"),
      
    },
     {
      equipment: "YEALINK T87",
      qty: getHardwareQty("YEALINK T87"),
     
    },
    

    {
      equipment: "GRANDSTREAM 3470",
      qty: hardwareItems.find((i) => i.name === "Grandstream 3470")?.quantity || 0,
    },

    {
      equipment: "GRANDSTREAM 3480",
      qty: hardwareItems.find((i) => i.name === "Grandstream 3480")?.quantity || 0,
    },

    {
      equipment: "CISCO 9871",
      qty: hardwareItems.find((i) => i.name === "Cisco 9871")?.quantity || 0,
    },
    {
      equipment: "Yealink IP Handsets",
      qty: getHardwareQty("Yealink IP Handsets", 1),
      feature: "Auto Attendant Recording",
      featureQty: getAdditionalHardwareQty("AA Voice Over"),
      service: "Email Reports",
      serviceQty: mobileAppUsers,
    },
    {
      equipment: "Netgear GS108LP Switch",
      qty: getAdditionalHardwareQty("Netgear GS108LP"),
      feature: "Voicemail ports",
      featureQty: getAdditionalHardwareQty("Voicemail ports"),
      service: "Call Recording",
      serviceQty: mobileAppUsers,
    },
    {
      equipment: "Draytek 2865LAC Router",
      qty: getAdditionalHardwareQty("Draytek 2865LAC"),
      feature: "Faxsmile Connection",
      featureQty: getAdditionalHardwareQty("Analogue Devices"),
      service: "2 line voice recording upgrade",
      serviceQty: 0,
    },
    {
      equipment: "UCM IP Hosted PBX",
      qty: getAdditionalHardwareQty("UCM IP PBX"),
      feature: "1 Button Entry",
      featureQty: getAdditionalHardwareQty("1 Button Entry"),
      service: "Callswitch desktop Client",
      serviceQty: mobileAppUsers,
    },
    {
      equipment: "Cat 5E Cables",
      qty: getAdditionalHardwareQty("Cat 5E Cable (5m)"),
      feature: "Pincode Door Entry",
      featureQty: getAdditionalHardwareQty("Pancode"),
      service: "Cti outlook integration",
      serviceQty: mobileAppUsers,
    },
    {
      equipment: "Dect Phone Repeaters",
      qty: getHardwareQty("Dect Phone Repeaters", 0),
      feature: "Fibre TV (Business only)",
      featureQty: getHardwareQty("Fibre TV/Wallboard"),
      service: "PC to Text",
      serviceQty: getHardwareQty("Bell / Tannoy"),
    },
    {
      equipment: "Integrated Dect Phone",
      qty: getHardwareQty("Yealink W73H DECT"),
      feature: "Fibre wall boards",
      featureQty: 0,
      service: "Softphone",
      serviceQty: 0,
    },
    {
      equipment: "Dect base stations",
      qty: getHardwareQty("IP DECT Base"),
      feature: "UPS",
      featureQty: getAdditionalHardwareQty("UPS"),
      service: "New cat 5 cabling points",
      serviceQty: getAdditionalHardwareQty("Cat 5"),
    },
    {
      equipment: "Analogue cordless",
      qty: getHardwareQty("Bell / Tannoy"),
      feature: "Surge Protection",
      featureQty: getAdditionalHardwareQty("Surge protection"),
      service: "Future system site move (excludes cabling and line move costs)",
      serviceQty: getAdditionalHardwareQty("System Moves"),
    },

    { service: "Fibre Web Listing Licence", serviceQty: 1 },
  ];
  const buildContractPdfBlob = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        // ✅ Clone DOM for clean rendering (same as download)
        const element = pdfRef.current.cloneNode(true);

        // ✅ Inject signature fields like download method
        const signatureSections =
          element.querySelectorAll(".signature-section");
        signatureSections.forEach((section, index) => {
          const sectionId =
            section.getAttribute("data-section") || `section_${index + 1}`;
          const signatureFields = section.querySelectorAll(".signature-field");
          const initialFields = section.querySelectorAll(".initial-field");

          // Handle signature fields
          signatureFields.forEach((field, fieldIndex) => {
            // Skip if already has input
            if (field.querySelector("input")) return;

            const input = document.createElement("input");
            input.type = "text";
            input.name = `${sectionId}_signature_${fieldIndex + 1}`;
            input.className = "adobe-sign-field signature-input";
            input.setAttribute("data-adobe-field-type", "signature");
            input.style.width = "100%";
            input.style.height = "100%";
            input.style.border = "1px solid #666";
            input.style.background = "transparent";
            input.placeholder = "Signature";
            input.readOnly = true;
            field.innerHTML = "";
            field.appendChild(input);
          });

          // Handle initial fields (including footer initials)
          initialFields.forEach((field, fieldIndex) => {
            // Skip if already has input
            if (field.querySelector("input")) return;

            // Use custom field name from data attribute (for footer initials) or generate one
            const fieldName =
              field.getAttribute("data-field-name") ||
              `${sectionId}_initial_${fieldIndex + 1}`;

            const input = document.createElement("input");
            input.type = "text";
            input.name = fieldName;
            input.className = "adobe-sign-field initial-input";
            input.setAttribute("data-adobe-field-type", "initial");
            input.style.width = "100%";
            input.style.height = "100%";
            input.style.border = "1px solid #666";
            input.style.background = "transparent";
            input.style.textAlign = "center";
            input.placeholder = "Initial";
            input.readOnly = true;
            field.innerHTML = "";
            field.appendChild(input);
          });
        });

        const options = {
          margin: [5, 5, 5, 5],
          filename: "fibre_telecoms_contract.pdf",
          image: { type: "jpeg", quality: 0.7 },
          html2canvas: {
            scale: 1.4,
            useCORS: true,
            allowTaint: true,
            logging: false,
            letterRendering: true,
          },
          jsPDF: {
            unit: "mm",
            format: [210, 297],
            orientation: "portrait",
            compress: true,
            putOnlyUsedFonts: true,
            hotfixes: ["px_scaling"],
          },
          pagebreak: { mode: ["avoid-all", "css", "legacy"] },
        };

        const arrayBuffer = await html2pdf()
          .set(options)
          .from(element)
          .outputPdf("arraybuffer");

        const uint8Array = new Uint8Array(arrayBuffer);
        uint8Array[15] = 0x00;

        const blob = new Blob([uint8Array], { type: "application/pdf" });

        resolve(blob);
      } catch (err) {
        reject(err);
      }
    });
  };

  const generatePDF = async () => {
      try {
        setIsGeneratingPDF(true);
        setIsPdfMode(true);

        await new Promise((resolve) => setTimeout(resolve, 300));

        const rawElement = pdfRef.current;
        if (!rawElement) {
          console.error("❌ PDF reference not found.");
          setIsGeneratingPDF(false);
          return;
        }

        // Clone and strip display-only elements
        const element = rawElement.cloneNode(true);
        element.querySelectorAll('.no-pdf-export').forEach(el => el.remove());

        const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #111;
              background: #fff;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${element.innerHTML}
        </body>
        </html>
      `;

        const token = localStorage.getItem("authToken");
        if (!token) {
          alert("Authentication token missing. Please log in again.");
          setIsGeneratingPDF(false);
          return;
        }

        const pdfHtmlContent = htmlContent
          .replace(/\{\{Sig1_es_:signer1:signature\}\}/g, "Signature Area")
          .replace(/\{\{Int1_es_:signer1:initials\}\}/g, "Initials Here");

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/pdf/generate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              html: pdfHtmlContent,
              filename: `contract_${formData.companyName || "document"}`,
            }),
          },
        );

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || "Failed to generate PDF");
        }

        const pdfBlob = await response.blob();

        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `contract_${formData.companyName || "document"}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);

        await QuoteProgressService.saveStep({
          quoteId: selectedConnectionId,
          stepName: "contract",
          completed: true,
          actions: { downloaded: true },
        });

        alert("✅ PDF generated successfully!");
      } catch (error) {
        console.error("❌ Error generating PDF:", error);
        alert("Error generating contract PDF. Please try again.");
      } finally {
        setIsGeneratingPDF(false);
        setIsPdfMode(false);
      }
    };

  // Helper function to add form fields to signature areas
  const addFormFieldsToSignatures = (clonedDoc) => {
    const signatureSections = clonedDoc.querySelectorAll(".signature-section");

    signatureSections.forEach((section, index) => {
      const sectionId =
        section.getAttribute("data-section") || `section_${index + 1}`;
      const signatureFields = section.querySelectorAll(".signature-field");
      const initialFields = section.querySelectorAll(".initial-field");

      // Handle signature fields
      signatureFields.forEach((field, fieldIndex) => {
        // Skip if already has input
        if (field.querySelector("input")) return;

        const input = document.createElement("input");
        input.type = "text";
        input.name = `${sectionId}_signature_${fieldIndex + 1}`;
        input.className = "adobe-sign-field signature-input";
        input.setAttribute("data-adobe-field-type", "signature");
        input.style.width = "100%";
        input.style.height = "100%";
        input.style.border = "1px solid #666";
        input.style.background = "transparent";
        input.placeholder = "Signature";
        input.readOnly = true;

        field.innerHTML = "";
        field.appendChild(input);
      });

      // Handle initial fields (including footer initials)
      initialFields.forEach((field, fieldIndex) => {
        // Skip if already has input
        if (field.querySelector("input")) return;

        // Use custom field name from data attribute (for footer initials) or generate one
        const fieldName =
          field.getAttribute("data-field-name") ||
          `${sectionId}_initial_${fieldIndex + 1}`;

        const input = document.createElement("input");
        input.type = "text";
        input.name = fieldName;
        input.className = "adobe-sign-field initial-input";
        input.setAttribute("data-adobe-field-type", "initial");
        input.style.width = "100%";
        input.style.height = "100%";
        input.style.border = "1px solid #666";
        input.style.background = "transparent";
        input.style.textAlign = "center";
        input.placeholder = "Initial";
        input.readOnly = true;

        field.innerHTML = "";
        field.appendChild(input);
      });
    });
  };

  const sendToDocuSign = async () => {
    const confirmSend = window.confirm(
      `The contract will be sent to: ${formData.companyEmail}\n\nDo you want to continue?`,
    );

    if (!confirmSend) return;
    setIsGeneratingPDF(true);

    try {
      // Grab the DOM element you want to convert
      const element = pdfRef.current;

      const opt = {
        margin: [10, 10, 10, 10],
        filename: "fibre_telecoms_contract.pdf",
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          letterRendering: true,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      };

      // ✅ Generate a proper PDF Blob
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");

      // ✅ Convert Blob → Base64
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      const hasMobileData =
        mobileData && Object.keys(mobileData).length > 0 ? 1 : 0;

      // ✅ Prepare data for backend
      const docusignData = {
        pdf_content: pdfBase64,
        signer_email: `${formData.companyEmail}`, // Replace with dynamic field
        signer_name: formData.managerName,
        email_subject: `Fibre Telecoms Contract for ${formData.companyName}`,
        email_body: `Dear ${formData.managerName},\n\nPlease review and sign the attached Fibre Telecoms contract.\n\nThank you.`,
        has_mobile_data: hasMobileData,
        has_rental: 0,
      };

      // ✅ Send to backend
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/docusign/send-contract`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify(docusignData),
        },
      );

      const result = await response.json();

      if (result.success) {
        alert("Contract sent for signature successfully!");
        await QuoteProgressService.saveStep({
          quoteId: selectedConnectionId,
          stepName: "contract",
          completed: true,
          actions: { emailed: true },
        });
      } else {
        alert("Failed to save quote progress: " + result.message);
      }
    } catch (error) {
      console.error("Error sending to DocuSign:", error);
      alert("Error sending contract for signature");
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  // Add this after your leaseCalculatorData useState declaration
useEffect(() => {
  leaseIdRef.current = leaseCalculatorData.leaseId;
}, [leaseCalculatorData.leaseId]);

  const sendToAdobeSign = async () => {
    if (!window.confirm(`Send to ${formData.companyEmail}?`)) return;

    setIsGeneratingAdobePDF(true);
    setIsPdfMode(true);
    setIsGeneratingPDF(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const rawElement = pdfRef.current;
      if (!rawElement) {
        throw new Error("PDF reference not found");
      }

      // Clone and strip display-only elements
      const element = rawElement.cloneNode(true);
      element.querySelectorAll('.no-pdf-export').forEach(el => el.remove());

      const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          body {
            font-family: Arial, Helvetica, sans-serif;
            color: #111;
            background: #fff;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${element.innerHTML}
      </body>
      </html>
    `;

      const hasMobileData =
        mobileData && Object.keys(mobileData).length > 0 ? 1 : 0;

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication token missing");
      }

      const body = {
        html: htmlContent,
        signer_email: formData.companyEmail,
        cc_email: currentUser?.data?.email,
        signer_name: formData.managerName,
        agreement_name: `Fibre Telecoms Contract - ${formData.companyName}`,
        email_subject: `Fibre Telecoms Contract for ${formData.companyName}`,
        email_body: `Dear ${formData.managerName},\n\nPlease review and sign the attached Fibre Telecoms contract.\n\nThank you.`,
        has_mobile_data: hasMobileData,
        connection_id: selectedConnectionId,
        has_rental: 0,
        sandbox: true,
        leaseId: leaseIdRef.current,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/pdf/generate-and-send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        },
      );

      const contentType = response.headers.get("content-type");

      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error(
          "Non-JSON response received:",
          textResponse.substring(0, 500),
        );
        throw new Error(
          `Server returned non-JSON response (Status ${response.status}). Check Laravel logs for details.`,
        );
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || result.error || "Failed to send contract",
        );
      }

      alert("✅ Contract sent for signature!");

      await QuoteProgressService.saveStep({
        quoteId: selectedConnectionId,
        stepName: "contract",
        completed: true,
        actions: { emailed: true },
      });
    } catch (error) {
      console.error("Adobe Send Error:", error);
      alert(`Error sending contract: ${error.message}`);
    } finally {
      setIsGeneratingAdobePDF(false);
      setIsPdfMode(false);
      setIsGeneratingPDF(false);
    }
  };


  useEffect(() => {
    if (selectedConnectionId) {
      checkAndHandleBankDetails();
    }
  }, [selectedConnectionId]);

  const checkAndHandleBankDetails = async () => {
    try {
      const response = await getBankDetails(selectedConnectionId);

      if (response?.data) {
        const { bank_name, sort_code, account_number } = response.data;

        // Show popup only if any field is null or undefined
        if (bank_name == null || sort_code == null || account_number == null) {
          setShowBankDetailsPopup(true);
        } else {
          setBankDetails(response.data);
          setShowBankDetailsPopup(false); // all fields exist, close popup
        }
      } else {
        // No bank details found → show popup
        setShowBankDetailsPopup(true);
      }
    } catch (error) {
      console.error("Failed to check bank details:", error);
      setShowBankDetailsPopup(true);
    }
  };

  const handleBankDetailsSave = async (details) => {
    setBankDetails(details); // update immediately so UI knows it's filled

    // Optionally refetch from server to be 100% in sync
    await checkAndHandleBankDetails();

    setShowBankDetailsPopup(false); // close popup after save
  };

  const getHardwareTotal = (name) => {
    const item = hardwareItems.find(i => i.name === name);
    return (item?.quantity || 0) + (item?.focQuantity || 0);
  };

  const getAdditionalTotal = (name) => {
    const item = additionalHardware.find(i => i.name === name);
    return (item?.quantity || 0) + (item?.focQuantity || 0);
  };

  return (
    <div>
      <BankDetailsPopup
        isOpen={showBankDetailsPopup}
        onClose={() => setShowBankDetailsPopup(false)}
        onSave={handleBankDetailsSave}
        selectedConnectionId={selectedConnectionId}
        getBankDetails={getBankDetails} // Your existing get function
        updateBankDetails={updateBankDetails} // Your existing save function
        bankValidationService={bankValidationService}
      />
      {!isViewOnly && (
        <>
          <div className="text-center">
            <h2 className="text-xl font-bold">CONTRACT OVERVIEW</h2>
          </div>
          <div className="flex justify-end">
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              <Download size={16} />
              Download PDF
            </button>
            <button
              onClick={sendToAdobeSign}
              disabled={isGeneratingAdobePDF}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 ml-2"
            >
              {isGeneratingAdobePDF ? "Sending..." : "Adobe e-Signature"}
            </button>
          </div>
        </>
      )}

      <div className="max-w-6xl mx-auto p-6 bg-white" ref={pdfRef}>

      {/* Cover Image - Full Page */}
       <div
        style={{
          width: "100%",
          margin: 0,
          padding: 0,
          lineHeight: 0,
          fontSize: 0,
        }}
      >
        <img
          src="https://quoting-tool.fibretelecomsuk.co.uk/images/contract.png"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            margin: 0,
            padding: 0,
          }}
          alt="Phone system illustration"
        />
      </div>

        {/* PRE-SALE CONTRACT SUMMARY PAGE */}
        {!isViewOnly && (
          <>
            {/* PAGE 1 - Title + Network Services Contract */}
            <div
              className="px-8 py-10 bg-white"
              style={{ pageBreakBefore: "always", breakBefore: "page" }}  
            >
              {/* Title Block */}
              <div className="text-center mb-10">
                <p className="text-sm font-bold tracking-widest text-gray-600 uppercase mb-1">Pre-Sale</p>
                <h1 className="text-3xl font-bold uppercase tracking-wide text-gray-900 mb-8">
                  Contract Summary
                </h1>
                <div className="mt-8 space-y-2">
                  <p className="text-base font-semibold text-gray-800">
                    CUSTOMER –{" "}
                    <span className="font-bold uppercase">
                      {formData.companyName || "(ENTER COMPANY NAME)"}
                    </span>
                  </p>
                  <p className="text-base font-semibold text-gray-800">
                    SUPPLIER – FIBRE TELECOMS UK LTD
                  </p>
                </div>
              </div>

              <hr className="border-t-2 border-gray-300 my-8" />

              {/* NETWORK SERVICES CONTRACT */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-6">
                  NETWORK SERVICES CONTRACT
                </h2>
                <div className="space-y-4 text-sm text-gray-800">
                  {/* Contract Term */}
                  <div className="flex gap-2">
                    <span className="font-bold min-w-[160px]">Contract Term –</span>
                    <span>
                      {leaseCalculatorData.paymentProfile
                        ? `${leaseCalculatorData.paymentProfile} months`
                        : "(Contract term in months)"}
                    </span>
                  </div>

                  {/* Services */}
                  <div>
                    <p className="font-bold mb-2">Services and Cost Summary –</p>
                    <div className="border border-gray-300 rounded overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">
                              Service
                            </th>
                            <th className="px-4 py-2 text-center font-semibold text-gray-700 border-b border-gray-300">
                              Qty
                            </th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-700 border-b border-gray-300">
                              Cost (excl. VAT)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculateAllCosts.serviceDetails.length > 0 ? (
                            calculateAllCosts.serviceDetails.map((service, index) => (
                              <tr
                                key={index}
                                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                              >
                                <td className="px-4 py-2 border-b border-gray-200">
                                  {service.service}
                                </td>
                                <td className="px-4 py-2 text-center border-b border-gray-200">
                                  {service.quantity}
                                </td>
                                <td className="px-4 py-2 text-right border-b border-gray-200">
                                  £{service.rate.toLocaleString()}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="px-4 py-3 text-center text-gray-500 italic">
                                No services configured
                              </td>
                            </tr>
                          )}
                          <tr className="bg-blue-50 font-bold">
                            <td className="px-4 py-2 border-t-2 border-blue-300" colSpan="2">
                              Monthly costs excl. VAT
                            </td>
                            <td className="px-4 py-2 text-right border-t-2 border-blue-300 text-blue-700">
                               £{calculateAllCosts.totalMonthly?.toLocaleString() ?? "0.00"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Early Termination */}
                  <div className="mt-6 p-4 border border-gray-300 rounded bg-gray-50">
                    <p className="font-bold mb-1">Early Termination –</p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Where the Customer terminates this Contract prior to the expiry of
                      the Agreed Term, the balance of the fees payable for the Service
                      for the remainder of the Agreed Term shall become immediately due.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* PAGE 2 - Order Form Contract + Maintenance Contract */}
            <div
              className="px-8 py-10 bg-white"
              style={{ pageBreakBefore: "always", breakBefore: "page" }}
            >
              {/* ORDER FORM CONTRACT */}
              <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-6">
                ORDER FORM CONTRACT
              </h2>

              <p className="text-xs font-semibold text-gray-600 italic mb-4 text-center">
                NOTE: THIS CAN EITHER BE THROUGH A 3RD PARTY FINANCE COMPANY OR FIBRE TELECOMS UK LTD DIRECTLY
              </p>

              <div className="space-y-4 text-sm text-gray-800 mb-8">
                {/* Contract Term */}
                <div className="flex gap-2">
                  <span className="font-bold min-w-[160px]">Contract Term –</span>
                  <span>
                    {leaseCalculatorData.paymentProfile
                      ? `${leaseCalculatorData.paymentProfile} months`
                      : "(Contract term in months)"}
                  </span>
                </div>

                {/* Equipment Being Provided */}
                <div>
                  <p className="font-bold mb-2">Equipment being provided –</p>
                  <div className="border border-gray-300 rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Qty</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Manufacturer</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Model</th>
                          <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        
                        {getHardwareTotal("Mobile App") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Mobile App")}</td>
                            <td className="px-4 py-2">GAMMA</td>
                            <td className="px-4 py-2">MOBILE</td>
                            <td className="px-4 py-2">MOBILE CLIENT APPLICATION</td>
                          </tr>
                        )}
                        {getHardwareTotal("T54W") > 0 && (
                       
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("T54W")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T54W</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        {getHardwareTotal("T57W") > 0 && (
                       
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("T57W")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T57W</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        
                        {getHardwareTotal("T58W") > 0 && (
                        
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("T58W")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T58W</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                         {getHardwareTotal("YEALINK T88") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("YEALINK T88")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T88</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        {getHardwareTotal("YEALINK T85") > 0 && (
                        
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("YEALINK T85")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T85</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                       
                        
                         {getHardwareTotal("YEALINK T87") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("YEALINK T87")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">T87</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        
                        {getHardwareTotal("Grandstream 3470") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Grandstream 3470")}</td>
                            <td className="px-4 py-2">GRANDSTREAM</td>
                            <td className="px-4 py-2">3470</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        
                        {getHardwareTotal("Grandstream 3480") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Grandstream 3480")}</td>
                            <td className="px-4 py-2">GRANDSTREAM</td>
                            <td className="px-4 py-2">3480</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        
                        {getHardwareTotal("Cisco 9871") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Cisco 9871")}</td>
                            <td className="px-4 py-2">CISCO</td>
                            <td className="px-4 py-2">9871</td>
                            <td className="px-4 py-2">IP DESK PHONE HANDSET</td>
                          </tr>
                        )}
                        
                        {getHardwareTotal("Yealink W73H DECT") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Yealink W73H DECT")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">Yealink W73H DECT</td>
                            <td className="px-4 py-2">IP DECT CORDLESS PHONE HANDSET</td>
                          </tr>
                        )}
                        {getHardwareTotal("IP DECT Base") > 0 && (
                       
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("IP DECT Base")}</td>
                            <td className="px-4 py-2">YEALINK</td>
                            <td className="px-4 py-2">IP DECT BASE</td>
                            <td className="px-4 py-2">IP DECT BASE STATION</td>
                          </tr>
                        )}
                        {getAdditionalTotal("UCM IP PBX") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getAdditionalTotal("UCM IP PBX")}</td>
                            <td className="px-4 py-2">UCM</td>
                            <td className="px-4 py-2">PBX</td>
                            <td className="px-4 py-2">UCM IP PBX</td>
                          </tr>
                        )}
                        
                        {getAdditionalTotal("Draytek 2865LAC") > 0 && (
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getAdditionalTotal("Draytek 2865LAC")}</td>
                            <td className="px-4 py-2">DRAYTEK</td>
                            <td className="px-4 py-2">WI-FI</td>
                            <td className="px-4 py-2">Draytek 2865LAC ROUTER</td>
                          </tr>
                        )}
                        {getAdditionalTotal("Netgear GS108LP") > 0 && (
                        
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getAdditionalTotal("Netgear GS108LP")}</td>
                            <td className="px-4 py-2">NETGEAR</td>
                            <td className="px-4 py-2">POE</td>
                            <td className="px-4 py-2">Netgear GS108LP SWITCH</td>
                          </tr>
                        )}
                        {getHardwareTotal("Wi-Fi Booster") > 0 && (
                       
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Wi-Fi Booster")}</td>
                            <td className="px-4 py-2">DRAYTEK</td>
                            <td className="px-4 py-2">WI-FI BOOSTER</td>
                            <td className="px-4 py-2">WI-FI ACCESS POINT</td>
                          </tr>
                        )}
                        {getHardwareTotal("Headsets") > 0 && (
                        
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-2">{getHardwareTotal("Headsets")}</td>
                            <td className="px-4 py-2">—</td>
                            <td className="px-4 py-2">HEADSET</td>
                            <td className="px-4 py-2">CORDLESS HEADSET</td>
                          </tr>
                        )}
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-2">1</td>
                          <td className="px-4 py-2">FIBRE</td>
                          <td className="px-4 py-2">INSTALLATION</td>
                          <td className="px-4 py-2">PHONE SYSTEM INSTALLATION</td>
                        </tr>
                        {getHardwareTotal("Mobile App") > 0 && (
                        
                          <>
                            <tr className="border-b border-gray-200">
                              <td className="px-4 py-2">{getHardwareTotal("Mobile App")}</td>
                              <td className="px-4 py-2">GAMMA</td>
                              <td className="px-4 py-2">LICENCE</td>
                              <td className="px-4 py-2">LIFETIME LICENCE</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="px-4 py-2">{getHardwareTotal("Mobile App")}</td>
                              <td className="px-4 py-2">TOLLRING</td>
                              <td className="px-4 py-2">BUSINESS</td>
                              <td className="px-4 py-2">CALL REPORTING</td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="px-4 py-2">{getHardwareTotal("Mobile App")}</td>
                              <td className="px-4 py-2">TOLLRING</td>
                              <td className="px-4 py-2">BUSINESS</td>
                              <td className="px-4 py-2">CALL RECORDING</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Monthly Rental Cost */}
                <div className="flex gap-2">
                  <span className="font-bold min-w-[200px]">Monthly rental cost excl. VAT –</span>
                  <span>
                    {leaseCalculatorData.adjustedRental
                      ? `£${leaseCalculatorData.adjustedRental}`
                      : "(Monthly rental amount)"}
                  </span>
                </div>

                {/* Network Settlement Rebate */}
                <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
                  <p className="font-bold mb-1">Network settlement rebate –</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    As part of the contract the supplier shall pay the sum of{" "}
                    <span className="font-bold">
                      £
                      {(
                        parseInt(leaseCalculatorData.networkPayment || 0, 10) *
                        Math.abs(
                          parseInt(
                            leaseCalculatorData.networkSettlementContribution ||
                              0,
                            10,
                          ),
                        )
                      ).toLocaleString()}
                    </span>{" "}
                    <span className="font-bold">(VAT exempt) </span> within the first 12 months of the contracts starting. This can be in a lump sum or paid over instalments.
                  </p>
                </div>

                {/* Early Termination */}
                <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
                  <p className="font-bold mb-1">Early Termination –</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Where the Customer terminates this Contract prior to the expiry of the
                    Agreed Term, the balance of the fees payable for the Service for the
                    remainder of the Agreed Term shall become immediately due.
                  </p>
                </div>
              </div>

              {/* DIVIDER between Order Form and Maintenance */}
              <hr className="border-t-2 border-gray-300 my-8" />

              {/* MAINTENANCE CONTRACT */}
              <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-6">
                MAINTENANCE CONTRACT
              </h2>

              <div className="space-y-4 text-sm text-gray-800">
                {/* Contract Term */}
                <div className="flex gap-2">
                  <span className="font-bold min-w-[160px]">Contract Term –</span>
                  <span>
                    {leaseCalculatorData.paymentProfile
                      ? `${leaseCalculatorData.paymentProfile} months (6 months free included)`
                      : "(Contract term in months with free months included)"}
                  </span>
                </div>

                {/* Monthly Cost */}
                <div className="flex gap-2">
                  <span className="font-bold min-w-[160px]">Monthly cost after free months excl. VAT –</span>
                  <span>
                    £{(() => {
                      const users = hardwareItems.reduce((total, item) => {
                        if (item.name === "Mobile App" || item.name === "Mobile App Addon") {
                          return total + (item.quantity ?? 0);
                        }
                        return total;
                      }, 0);
                      return (users * 13.25).toFixed(2);
                    })()}
                  </span>
                </div>

                {/* Services */}
                <div className="flex gap-2">
                  <span className="font-bold min-w-[160px]">Services –</span>
                  <span>To maintain and support the equipment set out in the order form.</span>
                </div>

                {/* Early Termination */}
                <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
                  <p className="font-bold mb-1">Early Termination –</p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Where the Customer terminates this Contract prior to the expiry of the
                    Agreed Term, the balance of the fees payable for the Service for the
                    remainder of the Agreed Term shall become immediately due.
                  </p>
                </div>
              </div>
            </div>

          
          {/* PAGE 3 - Line Rental + Price Increases + Customer Term Waiver */}
            <div
              className="px-8 py-6 bg-white"
              style={{ pageBreakBefore: "always", breakBefore: "page" }}
            >
              {/* LINE RENTAL AND BROADBAND CONTRACT */}
              <div className="mb-4">
                <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-4">
                  LINE RENTAL AND BROADBAND CONTRACT
                </h2>

                <div className="space-y-3 text-sm text-gray-800">
                  {/* Contract Term */}
                  <div className="flex gap-2">
                    <span className="font-bold min-w-[160px]">Contract Term –</span>
                    <span>
                      {leaseCalculatorData.paymentProfile
                        ? `${leaseCalculatorData.paymentProfile} months`
                        : "(Contract term in months)"}
                    </span>
                  </div>

                  <div className="bg-white rounded-md shadow-sm overflow-hidden print:border print:border-gray-300">
                    <div className="bg-blue-600 text-white px-3 py-1.5">
                      <h3 className="text-sm font-semibold">Contract Details</h3>
                    </div>
                    <table className="w-full border-collapse text-xs">
                      <tbody>
                        <tr className="bg-white">
                          <td className="p-1.5 border text-gray-700">Amount of PSTN / ISDN lines services</td>
                          <td className="p-1.5 border text-gray-700 text-center">{currentLines.analoguePstnLines}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-1.5 border text-gray-700">Amount of Broadband services</td>
                          <td className="p-1.5 border text-gray-700 text-center">{broadbandService}</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="p-1.5 border text-gray-700">Amount of 4G / 5G broadband services</td>
                          <td className="p-1.5 border text-gray-700 text-center">{new4GQty}</td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-1.5 border text-gray-700">Direct Debit billing</td>
                          <td className="p-1.5 border text-gray-700 text-center">Yes</td>
                        </tr>
                        <tr className="bg-white font-semibold">
                          <td className="p-1.5 border text-gray-800">Monthly cost of broadband services (excl VAT)</td>
                          <td className="p-1.5 border text-gray-700 text-center">£{broadbandServiceTotal?.rate}</td>
                        </tr>
                        <tr className="bg-gray-100">
                          <td colSpan="2" className="p-1.5 border text-gray-700">
                            <div className="font-semibold mb-0.5">Billing and Payments:- Please note.</div>
                            <p className="text-xs text-gray-600">
                              If during the life of the contract the customer changes payment or billing
                              method then the applicable charges will be added to subsequent invoices.
                            </p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Early Termination */}
                  <div className="p-3 border border-gray-300 rounded bg-gray-50">
                    <p className="font-bold mb-1">Early Termination –</p>
                    <p className="text-sm text-gray-700 leading-snug">
                      Where the Customer terminates this Contract prior to the expiry of the
                      Agreed Term, the balance of the fees payable for the Service for the
                      remainder of the Agreed Term shall become immediately due.
                    </p>
                  </div>
                </div>
              </div>

              <hr className="border-t-2 border-gray-300 my-4" />

              {/* PRICE INCREASES */}
              <div className="mb-4">
                <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-3">
                  PRICE INCREASES
                </h2>
                <p className="text-sm text-gray-700 leading-snug">
                  There are no planned price increases within any contract in this bundle pack. Should
                  the supplier raise the cost of any service within the minimum term this must be done
                  by giving at least one months' notice. The customer will then have a 30 day period
                  in which to exit the increased contract free of charge and without penalty.
                </p>
              </div>

              <hr className="border-t-2 border-gray-300 my-4" />

              {/* CUSTOMER TERM WAIVER */}
              <div className="mb-4">
                <h2 className="text-xl font-bold text-center underline tracking-wide text-gray-900 mb-3">
                  CUSTOMER TERM WAIVER
                </h2>

                <p className="text-sm text-gray-700 leading-snug mb-4">
                  As part of Ofcom's general conditions C1 it is stated that no small business of fewer
                  than 10 employees should enter into a contract greater than 24 months unless express
                  consent to waive this right is granted. By signing below you are giving Fibre Telecoms
                  UK Ltd express consent to increase this contract term.
                </p>

                <div className="space-y-3 text-sm text-gray-800">
                  {/* Company Name */}
                  <div className="flex gap-2 items-center">
                    <span className="font-bold min-w-[130px]">Company Name:</span>
                    <div className="flex-1 border-b border-gray-400 pb-1 min-h-[20px]">
                      {formData.companyName || " "}
                    </div>
                  </div>

                  {/* Print Name */}
                  <div className="flex gap-2 items-center">
                    <span className="font-bold min-w-[130px]">Print Name:</span>
                    <div className="flex-1 border-b border-gray-400 pb-1 min-h-[20px]">
                      {formData.managerName || " "}
                    </div>
                  </div>

                  {/* Signed - reduced height from h-16 to h-10 */}
                  <div className="flex gap-2 items-start">
                    <span className="font-bold min-w-[130px] pt-1">Signed:</span>
                    <div className="flex-1 border border-dashed border-gray-400 rounded h-10 flex items-center justify-center text-gray-400 text-xs bg-white">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-t-2 border-gray-300 mt-4" />
            </div>
          </>
        )}
        {/* Customer Letter of Authority (CLoA) Section */}
        {!isViewOnly && (
          <div className="px-4 py-3" style={{ pageBreakBefore: "always", breakBefore: "page" }}>
            <div className="text-center mb-2">
              <h1 className="text-base font-bold">
                Customer Letter of Authority (CLoA)
              </h1>
              <p className="text-[9px] italic">
                for the porting of numbers from one provider to another
              </p>
            </div>

            {/* Current Retailer / New Retailer Table */}
            <div className="border border-gray-400 mb-2">
              <div className="grid grid-cols-2">
                {/* Header Row */}
                <div className="bg-gray-200 p-1 font-bold text-center text-xs border-r border-b border-gray-400">
                  Current Retailer
                </div>
                <div className="bg-gray-200 p-1 font-bold text-center text-xs border-b border-gray-400">
                  New Retailer
                </div>

                {/* Name Row */}
                <div className="grid grid-cols-[60px_1fr] border-r border-b border-gray-400">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Name
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.currentRetailerName || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.currentRetailerName || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            currentRetailerName: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                        placeholder="e.g. BT"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-[60px_1fr] border-b border-gray-400">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Name
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.newRetailerName || "TELCOSWITCH"}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.newRetailerName || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            newRetailerName: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                        placeholder="TELCOSWITCH"
                      />
                    )}
                  </div>
                </div>

                {/* Address Row */}
                <div className="grid grid-cols-[60px_1fr] border-r border-b border-gray-400">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Address:
                  </div>
                  <div className="p-1 text-center">
                    {isPdfMode ? (
                      <div className="min-h-[32px] whitespace-pre-line text-[9px]">
                        {formData.currentRetailerAddress || " "}
                      </div>
                    ) : (
                      <textarea
                        value={formData.currentRetailerAddress || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            currentRetailerAddress: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-center resize-none text-[9px]"
                        rows={2}
                        placeholder="Enter address"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-[60px_1fr] border-b border-gray-400">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Address:
                  </div>
                  <div className="p-1 text-center">
                    {isPdfMode ? (
                      <div className="min-h-[32px] whitespace-pre-line text-[9px]">
                        {formData.newRetailerAddress ||
                          "98 GREAT NORTH ROAD\nLONDON\nN2 0NL"}
                      </div>
                    ) : (
                      <textarea
                        value={formData.newRetailerAddress || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            newRetailerAddress: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-center resize-none text-[9px]"
                        rows={2}
                        placeholder="98 GREAT NORTH ROAD&#10;LONDON&#10;N2 0NL"
                      />
                    )}
                  </div>
                </div>

                {/* Contact Email Row */}
                <div className="grid grid-cols-[60px_1fr] border-r border-gray-400">
                  <div className="p-0.5 border-r border-gray-400 bg-gray-100 text-center text-[8px] leading-tight">
                    Contact
                    <br />
                    Email
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.currentRetailerEmail || " "}
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={formData.currentRetailerEmail || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            currentRetailerEmail: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                        placeholder="Enter email"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-[60px_1fr]">
                  <div className="p-0.5 border-r border-gray-400 bg-gray-100 text-center text-[8px] leading-tight">
                    Contact
                    <br />
                    Email
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-blue-600 text-[9px]">
                        {formData.newRetailerEmail || "porting@telcoswitch.com"}
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={formData.newRetailerEmail || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            newRetailerEmail: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-blue-600 text-[9px]"
                        placeholder="porting@telcoswitch.com"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Site Address and Numbers to be Ported Table */}
            <div className="border border-gray-400">
              {/* Header Row */}
              <div className="grid grid-cols-[60%_40%]">
                <div className="bg-gray-200 p-1 font-bold text-center text-[9px] border-r border-b border-gray-400">
                  Site address to register against numbers
                </div>
                <div className="bg-gray-200 p-1 font-bold text-center text-[9px] border-b border-gray-400">
                  <div>Numbers to be Ported</div>
                  <div className="font-normal italic text-[8px]">
                    (Geo & non-Geo)
                  </div>
                </div>
              </div>

              {/* Continuation Note */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="p-0.5 text-center italic text-[8px] border-r border-gray-400">
                  (Use Continuation sheets for additional numbers and/or sites)
                </div>
                <div className="p-0.5"></div>
              </div>

              {/* Building Name / Number Row */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[8px] leading-tight">
                    Building Name
                    <br />/ Number
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[20px] text-[9px]">
                        {formData.addressLine1 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine1 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine1: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div className="p-1" rowSpan={5}>
                  {isPdfMode ? (
                    <div className="min-h-[100px] whitespace-pre-line text-[9px]">
                      {formData.numbersToBePorted || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.numbersToBePorted || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          numbersToBePorted: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-[9px]"
                      rows={5}
                      placeholder="Enter numbers to be ported"
                    />
                  )}
                </div>
              </div>

              {/* Street Name Row */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Street Name
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.addressLine2 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine2 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine2: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div></div>
              </div>

              {/* Town/City Row */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Town/City
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.addressLine3 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine3 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine3: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div></div>
              </div>

              {/* County Row */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    County
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.addressLine4 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine4 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine4: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div></div>
              </div>

              {/* Post Code Row */}
              <div className="grid grid-cols-[60%_40%] border-b border-gray-400">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[9px]">
                    Post Code
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.postcode || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.postcode || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, postcode: e.target.value })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div></div>
              </div>

              {/* MBN Row */}
              <div className="grid grid-cols-[60%_40%]">
                <div className="grid grid-cols-[90px_1fr]">
                  <div className="p-1 border-r border-gray-400 bg-gray-100 text-[7px] leading-tight">
                    MBN-Main Billing number-If known (Geo only)
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.cloaMBN || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.cloaMBN || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, cloaMBN: e.target.value })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
                <div></div>
              </div>
            </div>

            {/* Customer's Company Details Section */}
            <div className="mt-2">
              <div className="border border-gray-400">
                {/* Header */}
                <div className="bg-gray-200 p-1 text-center border-b border-gray-400">
                  <div className="font-bold text-xs">
                    Customer's Company Details
                  </div>
                  <div className="text-[8px] italic">
                    (as shown on most recent bill from current provider)
                  </div>
                </div>

                {/* Company Name Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    Company Name
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.companyName || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.companyName || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            companyName: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Billing Address Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    Billing Address
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {[formData.addressLine1, formData.addressLine2]
                          .filter(Boolean)
                          .join(", ") || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={[formData.addressLine1, formData.addressLine2]
                          .filter(Boolean)
                          .join(", ")}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine2: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Town/City Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    Town/City
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.addressLine3 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine3 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine3: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* County Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    County
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.addressLine4 || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.addressLine4 || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            addressLine4: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Post Code Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    Post Code
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.postcode || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={
                          formData.cloaCompanyPostCode ||
                          formData.postcode ||
                          ""
                        }
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cloaCompanyPostCode: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Company Registration No. Row */}
                <div className="grid grid-cols-[130px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[9px]">
                    Company Registration No.
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.cloaCompanyRegNo || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.cloaCompanyRegNo || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cloaCompanyRegNo: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Billing Account No. Row */}
                <div className="grid grid-cols-[130px_1fr]">
                  <div className="p-1 bg-gray-100 border-r border-gray-400 text-[8px]">
                    Billing Account No.
                    <span className="italic text-[7px]">(Non-Geo only)</span>
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.cloaBillingAccountNo || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.cloaBillingAccountNo || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cloaBillingAccountNo: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Declaration Text */}
              <div className="mt-2 text-[8px] text-justify space-y-1">
                <p>
                  <span className="font-bold underline">
                    Fao my current provider;
                  </span>{" "}
                  - this CLoA is to notify you that I (representing the customer
                  shown below) have taken the decision to move my Telephony
                  services to a new Provider and require the numbers associated
                  with those services to be ported across to my chosen new
                  Provider (stated above).
                </p>
                <p>
                  My new Provider is authorised to act on my behalf in this
                  matter & you have my authority to disclose to my new Provider
                  (at their request) any other service or site-specific details
                  they might need to allow this port to proceed (e.g.
                  Site/Billing address post code, DDI number range, Main Billing
                  Number (<span className="font-bold">MBN</span>), etc.).
                </p>
                <p>
                  I recognise that it is my responsibility to arrange the
                  cessation of, or changes to, any other services provided by my
                  current Provider.
                </p>
              </div>
            </div>

            {/* Requester's Details Section */}
            <div className="mt-2">
              <div className="border border-gray-400">
                {/* Header */}
                <div className="bg-gray-200 p-1 text-center font-bold text-xs border-b border-gray-400">
                  Requester's Details
                </div>

                {/* Signed Row */}
                <div className="grid grid-cols-[100px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-[9px]">
                    Signed
                  </div>
                  <div className="p-1">
                    <div className="adobe-sig w-full h-[24px] border border-dashed border-gray-300 flex items-center justify-center text-gray-500 bg-white text-[9px]">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                </div>

                {/* Print Name and Job Title Row */}
                <div className="grid grid-cols-[100px_1fr_70px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-[9px]">
                    Print Name
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.managerName || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.managerName || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            managerName: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-[9px]">
                    Job title
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.position || " "}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={formData.position || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, position: e.target.value })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Date and Email Row */}
                <div className="grid grid-cols-[100px_1fr_70px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-[8px]">
                    Date (DD/MM/YYYY)
                  </div>
                  <div className="p-1 border-r border-gray-400">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {normalizeToDDMMYYYY(formData.date)}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={normalizeToDDMMYYYY(formData.date)}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            date: formatDateDDMMYYYY(e.target.value),
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                        placeholder="DD/MM/YYYY"
                        maxLength={10}
                      />
                    )}
                  </div>
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-[9px]">
                    Email
                  </div>
                  <div className="p-1">
                    {isPdfMode ? (
                      <div className="min-h-[14px] text-[9px]">
                        {formData.companyEmail || " "}
                      </div>
                    ) : (
                      <input
                        type="email"
                        value={formData.companyEmail || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cloaEmail: e.target.value,
                          })
                        }
                        className="w-full border-none outline-none text-[9px]"
                      />
                    )}
                  </div>
                </div>

                {/* Validity Row */}
                <div className="grid grid-cols-[100px_1fr] border-b border-gray-400">
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-center font-semibold text-[9px]">
                    Validity
                  </div>
                  <div className="p-1 text-center text-[9px]">
                    This CLoA is valid for{" "}
                    <span className="font-bold">6 months</span> from the above
                    date
                  </div>
                </div>

                {/* Page Number Row */}
                <div className="grid grid-cols-[100px_1fr_50px_1fr]">
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-center font-semibold text-[9px]">
                    Page Number
                  </div>
                  <div className="p-1 text-center text-[9px] border-r border-gray-400">
                    1
                  </div>
                  <div className="p-1 bg-gray-200 border-r border-gray-400 text-center font-semibold text-[9px]">
                    OF
                  </div>
                  <div className="p-1 text-center text-[9px]">2</div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* CLoA Continuation Sheet - Page 2 */}
        {!isViewOnly && (
          <div  className="px-4 py-3" style={{ pageBreakBefore: "always", breakBefore: "page" }}>
            {/* Title */}
            <div className="text-center mb-4">
              <h1 className="text-xl font-bold">
                Customer Letter of Authority (CLoA)
              </h1>
              <p className="text-xs italic">
                for the porting of numbers from one provider to another
              </p>
            </div>

            {/* Customer Company Name */}
            <div className="border border-gray-400 mb-3">
              <div className="grid grid-cols-[160px_1fr]">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 font-semibold text-xs">
                  Customer Company Name
                </div>
                <div className="p-1.5">
                  {isPdfMode ? (
                    <div className="min-h-[18px] text-xs">
                      {formData.cloaCompanyName || formData.companyName || " "}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={
                        formData.cloaCompanyName || formData.companyName || ""
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaCompanyName: e.target.value,
                        })
                      }
                      className="w-full border-none outline-none text-xs"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Additional Sites and/or Numbers to be Ported Table */}
            <div className="border border-gray-400 mb-4">
              {/* Header */}
              <div className="bg-gray-200 p-1.5 text-center border-b border-gray-400">
                <div className="font-bold text-sm">
                  Additional Sites and/or Numbers to be Ported
                </div>
                <div className="text-[10px] italic">(continuation sheet)</div>
              </div>

              {/* Sub Header */}
              <div className="grid grid-cols-2 border-b border-gray-400">
                <div className="p-1.5 text-center font-semibold text-xs border-r border-gray-400">
                  Site Address(es)
                </div>
                <div className="p-1.5 text-center">
                  <div className="font-semibold text-xs">
                    Numbers to be Ported
                  </div>
                  <div className="text-[10px] italic">(Geo & non-Geo)</div>
                </div>
              </div>

              {/* Row 1 */}
              <div className="grid grid-cols-2 border-b border-gray-400">
                <div className="p-1.5 border-r border-gray-400 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalSite1 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalSite1 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalSite1: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
                <div className="p-1.5 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalNumbers1 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalNumbers1 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalNumbers1: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-2 border-b border-gray-400">
                <div className="p-1.5 border-r border-gray-400 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalSite2 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalSite2 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalSite2: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
                <div className="p-1.5 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalNumbers2 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalNumbers2 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalNumbers2: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid grid-cols-2 border-b border-gray-400">
                <div className="p-1.5 border-r border-gray-400 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalSite3 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalSite3 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalSite3: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
                <div className="p-1.5 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalNumbers3 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalNumbers3 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalNumbers3: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
              </div>

              {/* Row 4 */}
              <div className="grid grid-cols-2 border-b border-gray-400">
                <div className="p-1.5 border-r border-gray-400 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalSite4 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalSite4 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalSite4: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
                <div className="p-1.5 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalNumbers4 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalNumbers4 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalNumbers4: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
              </div>

              {/* Row 5 */}
              <div className="grid grid-cols-2">
                <div className="p-1.5 border-r border-gray-400 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalSite5 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalSite5 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalSite5: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
                <div className="p-1.5 min-h-[56px]">
                  {isPdfMode ? (
                    <div className="whitespace-pre-line text-xs">
                      {formData.cloaAdditionalNumbers5 || " "}
                    </div>
                  ) : (
                    <textarea
                      value={formData.cloaAdditionalNumbers5 || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaAdditionalNumbers5: e.target.value,
                        })
                      }
                      className="w-full h-full border-none outline-none resize-none text-xs"
                      rows={2}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Requester's Details Section - Page 2 */}
            <div className="border border-gray-400">
              {/* Header */}
              <div className="bg-gray-200 p-1.5 text-center font-bold text-sm border-b border-gray-400">
                Requester's Details
              </div>

              {/* Signed Row */}
              <div className="grid grid-cols-[120px_1fr] border-b border-gray-400">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-xs">
                  Signed
                </div>
                <div className="p-1.5">
                  <div className="adobe-sig w-full h-[28px] border border-dashed border-gray-300 flex items-center justify-center text-gray-500 bg-white text-xs">
                    {"{{Sig1_es_:signer1:signature}}"}
                  </div>
                </div>
              </div>

              {/* Print Name and Job Title Row */}
              <div className="grid grid-cols-[120px_1fr_80px_1fr] border-b border-gray-400">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-xs">
                  Print Name
                </div>
                <div className="p-1.5 border-r border-gray-400">
                  {isPdfMode ? (
                    <div className="min-h-[18px] text-xs">
                      {formData.cloaPrintName || formData.managerName || " "}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={
                        formData.cloaPrintName || formData.managerName || ""
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaPrintName: e.target.value,
                        })
                      }
                      className="w-full border-none outline-none text-xs"
                    />
                  )}
                </div>
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-xs">
                  Job title
                </div>
                <div className="p-1.5">
                  {isPdfMode ? (
                    <div className="min-h-[18px] text-xs">
                      {formData.cloaJobTitle || formData.position || " "}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.cloaJobTitle || formData.position || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cloaJobTitle: e.target.value,
                        })
                      }
                      className="w-full border-none outline-none text-xs"
                    />
                  )}
                </div>
              </div>

              {/* Date and Email Row */}
              <div className="grid grid-cols-[120px_1fr_80px_1fr] border-b border-gray-400">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-xs">
                  Date (DD/MM/YYYY)
                </div>
                <div className="p-1.5 border-r border-gray-400">
                  {isPdfMode ? (
                    <div className="min-h-[18px] text-xs">
                      {normalizeToDDMMYYYY(formData.date)}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={normalizeToDDMMYYYY(formData.date)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          date: formatDateDDMMYYYY(e.target.value),
                        })
                      }
                      className="w-full border-none outline-none text-xs"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                    />
                  )}
                </div>
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-xs">
                  Email
                </div>
                <div className="p-1.5">
                  {isPdfMode ? (
                    <div className="min-h-[18px] text-xs">
                      {formData.companyEmail || " "}
                    </div>
                  ) : (
                    <input
                      type="email"
                      value={formData.companyEmail || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, cloaEmail: e.target.value })
                      }
                      className="w-full border-none outline-none text-xs"
                    />
                  )}
                </div>
              </div>

              {/* Validity Row */}
              <div className="grid grid-cols-[120px_1fr] border-b border-gray-400">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-center font-semibold text-xs">
                  Validity
                </div>
                <div className="p-1.5 text-xs">
                  This CLoA is valid for{" "}
                  <span className="font-bold">6 months</span> from the above
                  date
                </div>
              </div>

              {/* Page Number Row */}
              <div className="grid grid-cols-[120px_1fr_60px_1fr]">
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-center font-semibold text-xs">
                  Page Number
                </div>
                <div className="p-1.5 text-center text-xs border-r border-gray-400">
                  2
                </div>
                <div className="p-1.5 bg-gray-200 border-r border-gray-400 text-center font-semibold text-xs">
                  OF
                </div>
                <div className="p-1.5 text-center text-xs">2</div>
              </div>
            </div>
          </div>
        )}
        {/* Header with Logo and Customer Details */}
        <div
          className="flex justify-between items-start mb-6 border-b pb-4"
          style={{ pageBreakBefore: "always" }}
        >
          <div>
            <div className="mt-2">
              <p className="font-semibold">{formData.managerName}</p>
              <p className="font-semibold">{formData.companyName}</p>
              <p className="whitespace-pre-line">{formData.fullAddress}</p>
            </div>
          </div>
          <div className="text-right">
            <img
              src="https://quoting-tool.fibretelecomsuk.co.uk/images/FibreLogo.jpg"
              alt="Company Logo"
              className="w-48 h-auto object-contain rounded-lg bg-white p-2"
            />
          </div>
        </div>

        <div className="space-y-8">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end", // Changed from 'space-between'
              alignItems: "center",
              padding: "15px",
              background: "#f8f9fa",
              borderRadius: "8px",
            }}
          >
            <span
              style={{ fontWeight: 700, color: "#2c3e50", fontSize: "1.1em" }}
            >
              Sales Person: {formData.salesName}
            </span>
          </div>
          {/* Line Requirements - Read Only */}
          <div className="border rounded overflow-hidden">
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              LINE SERVICES
            </div>
            <h2 className="text-lg font-semibold mb-4 text-center">
              LINE REQUIREMENTS
            </h2>

            {/* Line Requirements Section */}
            {(() => {
              const lineRequirementsItems = [
                {
                  label: "Existing ISDN2 Channels",
                  value: lineRequirements.existingIsdn2Channels,
                },
                {
                  label: "New / Conversion Isdn2 Channel",
                  value: lineRequirements.newConversionIsdn2Channel,
                },
                {
                  label: "Existing ISDN30 Channels",
                  value: lineRequirements.existingIsdn30Channels,
                },
                {
                  label: "Hosted Lifetime Licenses with SIP Trunk",
                  value: (currentLines.hostedLifetimeLicenses || 0) + (linesFoc.hostedLifetimeLicensesFocQty || 0),
                },
                {
                  label: "Analogue PSTN Lines",
                  value: currentLines.analoguePstnLines,
                },
                {
                  label: "ADSL / FTTC Broadband",
                  value: currentLines.adslFttcBroadband, // no FOC for FTTC
                },
                {
                  label: "New 4G / 5G Broadband",
                  value: (currentLines.new4g5gBroadband || 0) + (linesFoc.new4g5gBroadbandFocQty || 0),
                },
                {
                  label: "FTTP / SoGEA Broadband",
                  value: (currentLines.fttpSogeaBroadband || 0) + (linesFoc.fttpSogeaBroadbandFocQty || 0),
                },
              ];

              return (
                <div style={{ marginBottom: "24px" }}>
                  <div
                    style={{
                      display: "table",
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: "12px",
                    }}
                  >
                    {/* Create rows of 4 items each */}
                    {Array.from(
                      { length: Math.ceil(lineRequirementsItems.length / 4) },
                      (_, rowIndex) => (
                        <div key={rowIndex} style={{ display: "table-row" }}>
                          {lineRequirementsItems
                            .slice(rowIndex * 4, (rowIndex + 1) * 4)
                            .map((item, colIndex) => (
                              <div
                                key={`${rowIndex}-${colIndex}`}
                                style={{
                                  display: "table-cell",
                                  width: "25%",
                                  padding: "0",
                                  verticalAlign: "top",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "6px",
                                  backgroundColor: "white",
                                }}
                              >
                                <div style={{ padding: "12px" }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: "14px",
                                      fontWeight: "600",
                                      marginBottom: "8px",
                                      color: "#374151",
                                      minHeight: "42px",
                                      lineHeight: "1.3",
                                      textAlign: "left",
                                    }}
                                  >
                                    {item.label}
                                  </label>
                                  <div
                                    style={{
                                      width: "100%",
                                      border: "2px solid #d1d5db",
                                      borderRadius: "6px",
                                      padding: "12px",
                                      backgroundColor: "#f8fafc",
                                      fontSize: "18px",
                                      fontWeight: "700",
                                      textAlign: "center",
                                      minHeight: "48px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#1e40af",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                    }}
                                  >
                                    {item.value}
                                  </div>
                                </div>
                              </div>
                            ))}

                          {/* Fill empty cells in the last row if needed */}
                          {lineRequirementsItems.slice(
                            rowIndex * 4,
                            (rowIndex + 1) * 4,
                          ).length < 4 &&
                            Array.from(
                              {
                                length:
                                  4 -
                                  lineRequirementsItems.slice(
                                    rowIndex * 4,
                                    (rowIndex + 1) * 4,
                                  ).length,
                              },
                              (_, emptyIndex) => (
                                <div
                                  key={`empty-${rowIndex}-${emptyIndex}`}
                                  style={{
                                    display: "table-cell",
                                    width: "25%",
                                    padding: "0",
                                  }}
                                />
                              ),
                            )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Data Broadband Requirements Section */}
            <div>
              <h3 className="text-md font-semibold mb-3 text-gray-700 text-center">
                DATA BROADBAND REQUIREMENTS
              </h3>
              {(() => {
                const dataBroadbandItems = [
                  {
                    label: "Starlink Broadband",
                    value: (dataBroadband.starlinkBroadband || 0) + (linesFoc.starlinkBroadbandFocQty || 0),
                  },
                  {
                    label: "Leased Lines",
                    value: (dataBroadband.leasedLines || 0) + (linesFoc.leasedLinesFocQty || 0),
                  },
                ];

                return (
                  <div
                    style={{
                      display: "table",
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: "12px",
                    }}
                  >
                    <div style={{ display: "table-row" }}>
                      {dataBroadbandItems.map((item, colIndex) => (
                        <div
                          key={colIndex}
                          style={{
                            display: "table-cell",
                            width: "25%",
                            padding: "0",
                            verticalAlign: "top",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            backgroundColor: "white",
                          }}
                        >
                          <div style={{ padding: "12px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "600",
                                marginBottom: "8px",
                                color: "#374151",
                                minHeight: "42px",
                                lineHeight: "1.3",
                                textAlign: "left",
                              }}
                            >
                              {item.label}
                            </label>
                            <div
                              style={{
                                width: "100%",
                                border: "2px solid #d1d5db",
                                borderRadius: "6px",
                                padding: "12px",
                                backgroundColor: "#f8fafc",
                                fontSize: "18px",
                                fontWeight: "700",
                                textAlign: "center",
                                minHeight: "48px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#1e40af",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              }}
                            >
                              {item.value}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Fill remaining cells to maintain 4-column layout */}
                      {Array.from({ length: 2 }, (_, emptyIndex) => (
                        <div
                          key={`data-empty-${emptyIndex}`}
                          style={{
                            display: "table-cell",
                            width: "25%",
                            padding: "0",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Fixed Hardware Section */}
          <div
            className="border p-4 rounded"
            style={{ pageBreakBefore: "always" }}
          >
            <h2 className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              HARDWARE
            </h2>

            <div
              style={{
                display: "table",
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "8px",
              }}
            >
              {(() => {
                const dropdownModels = ['Grandstream 3470', 'Grandstream 3480', 'Cisco 9871'];
                const allItems = [...hardwareItems, ...additionalHardware].filter(item => {
                  if (dropdownModels.includes(item.name)) {
                    //return (item.quantity ?? 0) > 0;
                    return ((item.quantity ?? 0) + (item.focQuantity ?? 0)) > 0;
                  }
                  return true;
                });

                return Array.from(
                  { length: Math.ceil(allItems.length / 4) },
                  (_, rowIndex) => {
                    const rowItems = allItems.slice(rowIndex * 4, (rowIndex + 1) * 4);
                    const emptyCells = 4 - rowItems.length;

                    return (
                      <div key={rowIndex} style={{ display: "table-row" }}>
                        {rowItems.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              display: "table-cell",
                              width: "25%",
                              padding: "8px",
                              verticalAlign: "top",
                            }}
                          >
                            <div
                              style={{
                                textAlign: "center",
                                padding: "12px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                backgroundColor: "white",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              {/* Icon container */}
                              <div
                                style={{
                                  height: "64px",
                                  width: "64px",
                                  margin: "0 auto 12px auto",
                                  backgroundColor: "#f3f4f6",
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {item.icon.startsWith("/") || item.icon.startsWith("http") ? (
                                  <img
                                    src={item.icon}
                                    alt={item.name}
                                    style={{
                                      height: "100%",
                                      width: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <span style={{ fontSize: "24px" }}>{item.icon}</span>
                                )}
                              </div>

                              {/* Item name */}
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  marginBottom: "12px",
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  lineHeight: "1.2",
                                }}
                              >
                                {item.name}
                              </div>

                              {/* Quantity */}
                              <div
                                style={{
                                  width: "100%",
                                  border: "2px solid #d1d5db",
                                  borderRadius: "6px",
                                  padding: "12px",
                                  backgroundColor: "#f8fafc",
                                  fontWeight: "700",
                                  color: "#1e40af",
                                  fontSize: "18px",
                                  textAlign: "center",
                                  minHeight: "48px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {((item.quantity ?? 0) + (item.focQuantity ?? 0))}
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Fill empty cells in last row */}
                        {emptyCells > 0 &&
                          Array.from({ length: emptyCells }, (_, emptyIndex) => (
                            <div
                              key={`empty-${rowIndex}-${emptyIndex}`}
                              style={{
                                display: "table-cell",
                                width: "25%",
                                padding: "8px",
                              }}
                            />
                          ))}
                      </div>
                    );
                  }
                );
              })()}
            </div>
          </div>
          <div className="border rounded overflow-hidden mt-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              SOFTPHONES & MOBILE APPS
            </div>

            {/* Image / App preview */}
            <div className="flex justify-center p-4 bg-white">
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/softphone.png" // replace with your actual image path
                alt="Softphones & Mobile Apps"
                className="w-64 h-auto"
              />
            </div>

            {/* Description */}
            <div className="text-center p-3 border-t">
              <p className="text-sm font-medium text-gray-800">
                Improve customer service, increase productivity and save
                valuable time.
              </p>
            </div>

            {/* Metrics / Numbers */}
            <div className="grid grid-cols-2 border-t text-center text-sm font-medium">
              <div className="p-2 border-r">
                SoftPhone & Mobile app Users:{" "}
                <span className="font-bold">{mobileAppUsers}</span>
              </div>
              <div className="p-2">
                <label htmlFor="crmIntegration" className="mr-1">
                  CRM INTEGRATION:
                </label>
                <span className="font-bold">0</span>
              </div>
            </div>
          </div>
          <div className="border rounded overflow-hidden mt-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              CALL REPORTING
            </div>

            {/* Image / App preview */}
            <div className="flex justify-center p-4 bg-white">
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/report.jpg" // replace with your actual image path
                alt="Call Reporting"
                className="w-64 h-auto"
              />
            </div>

            {/* Description */}
            <div className="text-center p-3 border-t">
              <p className="text-sm font-medium text-gray-800">
                Never lose business from missed calls, enhance customer service
                and measure the success of your advertising.
              </p>
            </div>

            {/* Metrics / Numbers */}
            <div className="grid grid-cols-1 border-t text-center text-sm font-medium">
              <div className="p-2 border-r">
                CallSwitch Call Reporting:{" "}
                <span className="font-bold">{mobileAppUsers}</span>
              </div>
            </div>
          </div>
          <div className="border rounded overflow-hidden mt-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              CALL RECORDING
            </div>

            {/* Image / App preview */}
            <div className="flex justify-center p-4 bg-white">
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/record.png" // replace with your actual image path
                alt="Call Reporting"
                className="w-64 h-auto"
              />
            </div>

            {/* Metrics / Numbers */}
            <div className="grid grid-cols-1 border-t text-center text-sm font-medium">
              <div className="p-2 border-r">
                CallSwitch Call Recording:{" "}
                <span className="font-bold">{mobileAppUsers}</span>
              </div>
            </div>
          </div>

          {/* Monthly Charges */}
          <div className="border rounded-lg shadow-lg bg-white">
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg no-pdf-export">
              COST COMPARISON
            </div>
             <div className="text-xs font-normal mt-2 bg-yellow-400 text-yellow-900 rounded-full px-3 py-1 inline-block no-pdf-export">
              🔒 Internal Use Only — Not included in downloaded or sent contract
            </div>

            <div className="p-6 space-y-6 text-gray-800">
              {/* CURRENT COSTS */}
               <div className="cost-section no-pdf-export">
                <h3 className="text-lg font-bold border-b pb-2 mb-3">
                  Current Monthly Costs (Excl VAT)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Current Phone System Lease Rental</span>
                    <span>£{leaseCalculatorData.current_lease_rental}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Mobile Phone Charges</span>
                    <span>£{leaseCalculatorData.current_mobile_charges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Call Charges</span>
                    <span>£{leaseCalculatorData.currentCallCharges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Service Charges</span>
                    <span>£{leaseCalculatorData.current_service_charges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Maintenance</span>
                    <span>£{leaseCalculatorData.current_maintenance}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Other Charges</span>
                    <span>£{serviceCharges.utilities}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Current Digital Service Charges</span>
                    <span>£{leaseCalculatorData.webHostingCost}</span>
                  </div>
                  
                  <div className="flex justify-between font-bold border-t pt-2">
                    <span>Total Current Cost</span>
                    <span>
                      £
                      {(
                        parseFloat(
                          leaseCalculatorData.current_total_cost ?? 0,
                        ) +
                        (leaseCalculatorData.webHostingCost
                          ? parseFloat(leaseCalculatorData.webHostingCost)
                          : 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* NEW COSTS */}
              <div className="cost-section no-pdf-export">
                <h3 className="text-lg font-bold border-b pb-2 mb-3">
                  New Monthly Charges (Excl VAT)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Telephone System Lease Rental</span>
                    <span>£{leaseCalculatorData.adjustedRental}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estimated Mobile Charges</span>
                    <span>£{leaseCalculatorData.estimated_mobile_charges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Estimated Call Charges</span>
                    <span>£{leaseCalculatorData.estimatedCallCharges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>New Service Charges</span>
                    <span>£{leaseCalculatorData.new_service_charges}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Network Settlement Contribution (PAID IN LUMP SUM - VAT EXEMPT. Figure showing per month pro rata)</span>
                    <span>
                      £{leaseCalculatorData.networkSettlementContribution}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Leased Line</span>
                    <span>£{leaseCalculatorData.leased_line}</span>
                  </div>
                 
                  <div className="flex justify-between">
                    <span>Digital Service Charges</span>
                    <span>£{leaseCalculatorData.totalNewDigitalCost}</span>
                  </div>
                   <div className="flex justify-between">
                    <span>IT Service Charge</span>
                    <span>£{leaseCalculatorData.it_service_charge}</span>
                  </div>
                   <div className="flex justify-between">
                    <span>IT Services / Microsoft Licences</span>
                    <span>£{(leaseCalculatorData.it_services_total || 0).toFixed(2)}</span>
                  </div>
                  {leaseCalculatorData.ai_receptionist_enabled && (
                    <div>
                      <div className="flex justify-between">
                        <span>AI Receptionist</span>
                        <span>£{leaseCalculatorData.ai_receptionist_charge.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-amber-700">
                        * Inbound calls charged at 15p per minute
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between font-bold border-t pt-2">
                    <span>Introductory new monthly costs</span>
                    <span>£{newChargesTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* DIFFERENCE */}
              <div className="cost-section bg-gr-50 p-4 rounded-md bg-gradient-to-r from-emerald-700 to-emerald-500 no-pdf-export">
                <div className="flex justify-between text-lg font-bold text-white">
                  <span>Monthly Difference</span>
                  <span>
                    £
                    {Math.abs(
                      newChargesTotal -
                        (parseFloat(
                          leaseCalculatorData.current_total_cost ?? 0,
                        ) +
                          (leaseCalculatorData.webHostingCost
                            ? parseFloat(leaseCalculatorData.webHostingCost)
                            : 0)),
                    ).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between mt-2 text-white">
                  <span>Upfront Equipment & Installation</span>
                  <span>£0.00</span>
                </div>
                <div className="flex justify-between mt-2 text-white font-bold">
                  <span>Monthly price after lump sum credit</span>
                  <span>
                    £{(
                      (leaseCalculatorData.adjustedRental ?? 0) +
                      (leaseCalculatorData.new_service_charges ?? 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
              {/* QUOTATION INCLUDES */}
              <div className="border border-grey mt-6 font-bold">
                <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg uppercase">
                  Quotation Includes
                </div>
                <table className="w-full text-sm border-t border-black">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2">Line order charges</td>
                      <td className="px-3 py-2 text-right">£0.00</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">
                        Acceptance that no 1 page summary document is required
                      </td>
                      <td className="px-3 py-2 text-right">✅</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">
                        Full installation and onsite staff training
                      </td>
                      <td className="px-3 py-2 text-right">✅</td>
                    </tr>

                  </tbody>
                </table>
              </div>

              {/* EQUIPMENT RENTAL TERMS */}
              <div className="border border-grey mt-6 font-bold">
                <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg uppercase">
                  Equipment Rental Terms
                </div>
                <table className="w-full text-sm border-t border-black">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2">Monthly rental period</td>
                      <td className="px-3 py-2 text-right ">
                        {leaseCalculatorData.paymentProfile} months
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">Monthly rental amount</td>
                      <td className="px-3 py-2 text-right ">
                        £{leaseCalculatorData.adjustedRental}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 bg-gray-50 ">
                        Excl. VAT per month (VAT charged at the prevailing rate)
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div></div>
              <div className="border p-4 rounded bg-gray-50">
                <h2 className="text-lg font-semibold mb-4">
                  SIGNATURES & AUTHORIZATION
                </h2>
                <div className="space-y-4">
                  <p className="text-sm font-medium">
                    BY SIGNING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
                    ACCORDANCE WITH THE OFCOM RULING REGARDING SMALL BUSINESSES
                    SUMMARY PAGE AND CONTRACT TERM.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Name & Position
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.managerName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Company
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.companyName}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Signature
                      </label>
                      <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                        {"{{Sig1_es_:signer1:signature}}"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Date
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.date}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 items-center">
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/draytek.png"
                alt="Draytek"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/openreach.png"
                alt="Open Reach"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/ipecs.png"
                alt="Ipecs"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/tollring.png"
                alt="Tollring"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/grandstream.jpg"
                alt="Grandstream"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/samsung.png"
                alt="Samsung"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
              <img
                src="https://quoting-tool.fibretelecomsuk.co.uk/images/gamma.png"
                alt="Gamma"
                className="h-10 w-auto object-contain rounded bg-white p-1"
              />
            </div>
          </div>

          {/* Network Services */}
          <div className=" border border-grey mt-6 font-bold">
            <div
              className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg uppercase"
              style={{ pageBreakBefore: "always", printColorAdjust: "exact" }}
            >
              NETWORK SERVICES
            </div>

            {/* Customer Information Section */}
            <div className="mb-8 bg-white rounded-xl shadow-md p-8 border border-gray-100">
              {/* Top Section - Company Addresses */}
              <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
                {/* Left Column - Client Company */}
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2 uppercase tracking-wide">
                      Client Company
                    </h3>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      Date :{" "}
                      {formData.date
                        ? formatToDDMMYYYY(formData.date)
                        : "Not specified"}
                    </p>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      {formData.companyName}
                    </p>
                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {formData.fullAddress}
                    </p>
                  </div>
                </div>

                {/* Right Column - Fibre Telecoms */}
                <div className="flex flex-col space-y-6">
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Fibre Telecoms Ltd{" "}
                      <span className="ml-2 text-blue-700">
                        (
                        {(
                          leaseCalculatorData.salesRepCommission / 1000
                        ).toFixed(2)}
                        )
                      </span>
                    </h3>
                    <div className="space-y-1 text-gray-800 leading-relaxed">
                      <p className="font-semibold">Hercules House</p>
                      <p className="font-semibold">Merlin Quay</p>
                      <p className="font-semibold">Hazel Road</p>
                      <p className="font-semibold">Southampton</p>
                      <p className="font-semibold">SO19 7GB</p>
                      <p className="mt-3 font-medium">Mobile: 0330 094 8797</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Section - Contact Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:gap-6 print:text-black">
                {/* Left Column - Contact Details */}
                <div className="space-y-6 print:space-y-4">
                  <div className="bg-white print:bg-white border print:border-gray-400 rounded-lg print:rounded-none p-6 print:p-4 shadow-sm print:shadow-none">
                    <h3 className="text-lg font-bold text-gray-800 mb-6 print:mb-4 print:text-base border-b border-gray-200 print:border-gray-400 pb-2">
                      Contact Information
                    </h3>

                    <div className="space-y-5 print:space-y-3">
                      {/* Phone Number */}
                      <div className="grid grid-cols-1 print:grid-cols-3 gap-2 print:gap-1 items-start print:items-center py-2 print:py-1 border-b border-gray-100 print:border-gray-300 last:border-b-0">
                        <div className="font-semibold text-gray-700 print:text-gray-900 text-sm print:text-xs print:col-span-1">
                          Main Company Phone:
                        </div>
                        <div className="text-gray-900 font-medium text-base print:text-sm print:col-span-2 print:text-left">
                          {formData.telephone}
                        </div>
                      </div>

                      {/* Directors Email */}
                      <div className="grid grid-cols-1 print:grid-cols-3 gap-2 print:gap-1 items-start print:items-center py-2 print:py-1 border-b border-gray-100 print:border-gray-300 last:border-b-0">
                        <div className="font-semibold text-gray-700 print:text-gray-900 text-sm print:text-xs print:col-span-1">
                          Directors Email:
                        </div>
                        <div className="text-blue-600 print:text-gray-900 font-medium bg-blue-50 print:bg-gray-100 p-3 print:p-1 rounded print:rounded-none break-all text-sm print:text-xs print:col-span-2 print:border print:border-gray-400">
                          {formData.companyEmail}
                        </div>
                      </div>

                      {/* Billing Email */}
                      <div className="grid grid-cols-1 print:grid-cols-3 gap-2 print:gap-1 items-start print:items-center py-2 print:py-1 border-b border-gray-100 print:border-gray-300 last:border-b-0">
                        <div className="font-semibold text-gray-700 print:text-gray-900 text-sm print:text-xs print:col-span-1">
                          Billing Email:
                        </div>
                        <div className="text-blue-600 print:text-gray-900 font-medium bg-blue-50 print:bg-gray-100 p-3 print:p-1 rounded print:rounded-none break-all text-sm print:text-xs print:col-span-2 print:border print:border-gray-400">
                          {formData.billingEmail}
                        </div>
                      </div>

                      {/* 24hr Service */}
                      <div className="grid grid-cols-1 print:grid-cols-3 gap-2 print:gap-1 items-start print:items-center py-2 print:py-1 border-b border-gray-100 print:border-gray-300 last:border-b-0">
                        <div className="font-semibold text-gray-700 print:text-gray-900 text-sm print:text-xs print:col-span-1">
                          24hr Service:
                        </div>

                        <div className="print:col-span-2">
                          {isGeneratingPDF || isViewOnly ? ( // Add isViewOnly check here
                            // ✅ Plain text only for PDF or View Mode
                            <div className="text-sm print:text-xs text-gray-800 print:text-gray-900 font-medium">
                              {formData.twentyFourHourService === "yes" ||
                              formData.twentyFourHourService === "Yes"
                                ? "Yes"
                                : "No"}
                            </div>
                          ) : (
                            <select
                              value={formData.twentyFourHourService || "no"}
                              onChange={(e) =>
                                handleInputChange(
                                  "twentyFourHourService",
                                  e.target.value,
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white"
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          )}
                        </div>
                      </div>

                      {/* Agreement Term */}
                      <div className="grid grid-cols-1 print:grid-cols-3 gap-2 print:gap-1 items-start print:items-center py-2 print:py-1">
                        <div className="font-semibold text-gray-700 print:text-gray-900 text-sm print:text-xs print:col-span-1">
                          Agreement Term:
                        </div>
                        <div className="text-blue-600 print:text-gray-900 font-medium text-base print:text-sm print:col-span-2">
                          {leaseCalculatorData.paymentProfile
                            ? `${leaseCalculatorData.paymentProfile / 12} years`
                            : "Not specified"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Company Details */}
                <div className="space-y-6 print:space-y-4">
                  {/* Company Details */}
                  <div className="bg-gray-50 print:bg-white p-6 print:p-4 rounded-lg print:rounded-none border border-gray-200 print:border-gray-400 shadow-sm print:shadow-none">
                    <h3 className="text-lg font-bold text-gray-800 print:text-gray-900 mb-4 print:mb-3 print:text-base uppercase tracking-wide border-b border-gray-200 print:border-gray-400 pb-2">
                      Company Details
                    </h3>
                    <div className="space-y-3 print:space-y-2 text-gray-800 print:text-gray-900 leading-relaxed print:leading-normal">
                      <div className="py-1">
                        <span className="font-semibold text-sm print:text-xs">
                          VAT Registration No:
                        </span>{" "}
                        <span className="font-medium text-sm print:text-xs">
                          307 5944 89
                        </span>
                      </div>
                      <div className="py-1">
                        <span className="font-semibold text-sm print:text-xs">
                          Registered Office:
                        </span>{" "}
                        <span className="font-medium text-sm print:text-xs">
                          Kingfisher House, Hampshire, SO52 9LP
                        </span>
                      </div>
                      <div className="py-1">
                        <span className="font-semibold text-sm print:text-xs">
                          Registered in England No:
                        </span>{" "}
                        <span className="font-medium text-sm print:text-xs">
                          11276097
                        </span>
                      </div>
                      <div className="py-1">
                        <span className="font-semibold text-sm print:text-xs">
                          Contact Number:
                        </span>{" "}
                        <span className="font-medium text-sm print:text-xs">
                          0330 094 8797
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tariff Information */}
                  <div className="bg-blue-50 print:bg-gray-100 p-6 print:p-4 rounded-lg print:rounded-none border border-blue-200 print:border-gray-400 shadow-sm print:shadow-none">
                    <h3 className="text-lg font-bold text-blue-800 print:text-gray-900 mb-2 print:mb-1 print:text-base">
                      Service Plan
                    </h3>
                    <div className="text-blue-700 print:text-gray-900 font-semibold text-base print:text-sm">
                      Tariff:{" "}
                      <span className="font-bold print:font-semibold">
                        FIBRE SIP BUNDLE
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Selection Sections */}
            <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm">
              {/* Fibre Undertaking */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  In return for the agreed term of service, Fibre undertakes to:
                </h3>
                <p className="text-xs text-gray-700 leading-relaxed">
                  Route all telephone numbers and/or lines relating to the above
                  address via the Fibre network, at the agreed rates. (
                  <span className="text-blue-600">
                    Please see tariff at www.fibretelecomsuk.co.uk
                  </span>
                  )
                </p>
              </div>

              {/* Agreement Start */}
              <div className="text-sm text-gray-800 mb-6">
                The agreement shall commence on the date of this form for the
                term stated above.
              </div>

              {/* Customer Obligations */}
              <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  In return for the above, the Customer agrees to:
                </h3>
                <ul className="list-disc list-inside space-y-2 text-xs text-gray-700 leading-relaxed">
                  <li>Pay all charges on time by direct debit.</li>
                  <li>
                    Repay in full any termination charges paid by us on behalf
                    of the Customer to a previous supplier should the Customer
                    wish to end this Agreement prior to the agreed term.
                  </li>
                </ul>
                <p className="text-xs text-gray-600 italic mt-3">
                  Please Note: The numbers below have been given to us by the
                  customer and by signing this form you agree that they are
                  correct and under your ownership.
                </p>
                <p className="text-xs text-gray-600  mt-3">
                  An annual admin fee of £199 + vat will be chargeable on each
                  December service charge invoice.
                </p>
              </div>
            </div>

            {/* Service Details Table */}
            {/* Telephone Line Details (stay on current page) */}
            <div className="print-container mb-8">
              <div className="bg-white rounded-lg shadow-md overflow-hidden print:shadow-none print:border print:border-gray-300">
                <div className="bg-blue-600 text-white px-4 py-3 print:bg-blue-800">
                  <h3 className="text-lg font-semibold print:text-base">
                    Telephone Line Details
                  </h3>
                </div>
                <div className="overflow-x-auto print:overflow-visible">
                  <table className="min-w-full print:min-w-0">
                    <thead>
                      <tr className="bg-gray-100 print:bg-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          Telephone
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          Postcode if homeworker
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          No of Lines
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 print:divide-gray-300">
                      {telephoneLines && telephoneLines.length > 0 ? (
                        telephoneLines.map((line, idx) => (
                          <tr
                            key={idx}
                            className={
                              idx % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50 print:bg-gray-100"
                            }
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 print:px-2 print:py-1 print:text-xs">
                              {line.existing_tel_no || "N/A"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 print:px-2 print:py-1 print:text-xs">
                              {line.postcode || "N/A"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 print:px-2 print:py-1 print:text-xs">
                              {line.quantity || "N/A"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 print:px-2 print:py-1 print:text-xs">
                              {line.type || "N/A"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="bg-gray-50 print:bg-gray-100">
                          <td
                            colSpan="4"
                            className="px-4 py-6 text-center text-sm text-gray-500 italic print:px-2 print:py-4 print:text-xs"
                          >
                            No telephone line details added
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Service Items (always start on a new page using inline style) */}
            <div
              className="print-container"
              style={{ pageBreakBefore: "always", breakBefore: "page" }}
            >
              <div className="bg-white rounded-lg shadow-md overflow-hidden print:shadow-none print:border print:border-gray-300">
                <div className="bg-blue-600 text-white px-4 py-3 print:bg-blue-800">
                  <h3 className="text-lg font-semibold print:text-base">
                    Service Items
                  </h3>
                </div>
                <div className="overflow-x-auto print:overflow-visible">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-100 print:bg-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          SERVICE ITEMS
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          QUANTITY
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider border-b print:px-2 print:py-1 print:text-[10px]">
                          TOTAL/EACH/QTY
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 print:divide-gray-300">
                      {calculateAllCosts.serviceDetails.map(
                        (service, index) => (
                          <tr
                            key={index}
                            className={
                              index % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50 print:bg-gray-100"
                            }
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 print:px-2 print:py-1 print:text-xs">
                              {service.service}
                            </td>
                            <td className="px-4 py-3 text-center print:px-2 print:py-1">
                              <div className="inline-block bg-gray-100 text-gray-700 rounded-md px-3 py-1 text-sm font-medium print:bg-transparent print:border print:border-gray-300 print:rounded-sm print:px-1 print:py-0 print:text-xs">
                                {service.quantity}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right print:px-2 print:py-1 print:text-xs">
                              £{service.rate.toLocaleString()}
                            </td>
                          </tr>
                        ),
                      )}

                      <tr className="bg-blue-50 font-bold print:bg-blue-100">
                        <td className="px-4 py-3 text-sm border-t-2 border-blue-200 print:px-2 print:py-1 print:text-xs">
                          Total monthly Fibre Service charges excl VAT
                        </td>
                        <td className="px-4 py-3 border-t-2 border-blue-200 print:px-2 print:py-1"></td>
                        <td className="px-4 py-3 text-right text-sm border-t-2 border-blue-200 print:px-2 print:py-1 print:text-xs">
                          £
                         {calculateAllCosts.totalMonthly?.toLocaleString() ?? "0.00"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div
              className="mb-6 p-6 bg-white rounded-lg shadow-md "
              style={{ pageBreakBefore: "always" }}
            >
              <div className="mb-4 text-sm text-gray-700 leading-relaxed">
                <p className="mb-3">
                  Service charges attached include 5000 minutes per user of local, national and mobile calls only, any calls made outside of this will be charged in line with our rate card. The rate card can be found on our website www.fibretelecomsuk.co.uk
                </p>
                <p className="mb-3">
                  I hereby give authorisation to Fibre Communications Limited to
                  deal with my account for the purpose of providing Least Cost
                  Routing/CPS and/or transferring my phone lines to Fibre. I
                  confirm that I am duly authorised to sign this binding
                  document as an authorised representative of the Company.
                </p>
                <p>
                  I have read, understood and I acknowledge acceptance of the
                  terms and conditions attached to the docusign, a full version
                  of which are available for viewing at{" "}
                  <a
                    href="https://www.fibretelecomsuk.co.uk"
                    className="text-blue-600 hover:underline"
                  >
                    www.fibretelecomsuk.co.uk
                  </a>
                </p>
              </div>
            </div>

            {/* Legal Text
          <div className="text-xs text-center">
            <strong>BY INITIALIZING THIS DOCUMENT, YOU AGREE TO WAIVE YOUR RIGHTS IN ACCORDANCE WITH THE NEW FSOC RECOM BRINGSOURCANDS/DEALS GUIDELINES AND CONTRACT TERMS.</strong><br />
            THIS ALSO INCLUDES THE 7 PAGE STANDARD PACE SCHEDULES TO ACCOMPANY THIS PLACEMENT.
          </div> */}

            <div className="border p-4 rounded bg-gray-50">
              <h2 className="text-lg font-semibold mb-4">
                SIGNATURES & AUTHORIZATION
              </h2>
              <div className="space-y-4">
                <p className="text-sm font-medium">
                  BY SIGNING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
                  ACCORDANCE WITH THE OFCOM RULING REGARDING SMALL BUSINESSES
                  SUMMARY PAGE AND CONTRACT TERM.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name & Position
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.managerName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Company
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.companyName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Signature
                    </label>
                    <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.date}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PageFooter
              pageNumber={1}
              totalPages={5}
              showInitials={true}
              sectionTitle="INITIALS"
            />

            <p className="mt-2 text-xs text-gray-700">
              BY INITIALLING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
              ACCORDANCE WITH THE NEW EECC OFCOM RULINGREGARDING SMALL
              BUSINESSES AND CONTRACT TERMS.{" "}
            </p>
            <p className="mt-2 mb-2 text-xs text-gray-700">
              THIS ALSO INCLUDES THE 1 PAGE SUMMARY PAGE REQUIRED TO ACCOMPANY
              THIS DOCUMENT.
            </p>
          </div>

          {/* Order form */}
          <div
            className="border border-grey mt-6 font-bold"
            style={{ pageBreakBefore: "always", printColorAdjust: "exact" }}
          >
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg uppercase">
              ORDER FORM
            </div>

            {/* Customer Information Section */}
            <div className="mb-8 bg-white rounded-xl shadow-md p-8 border border-gray-100">
              {/* Top Section - Company Addresses */}
              <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
                {/* Left Column - Client Company */}
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2 uppercase tracking-wide">
                      Client Company
                    </h3>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      Date :{" "}
                      {formData.date
                        ? formatToDDMMYYYY(formData.date)
                        : "Not specified"}
                    </p>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      {formData.companyName}
                    </p>
                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {formData.fullAddress}
                    </p>
                  </div>
                </div>

                {/* Right Column - Fibre Telecoms */}
                <div className="flex flex-col space-y-6">
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Fibre Telecoms(uk)Ltd
                    </h3>
                    <div className="space-y-1 text-gray-800 leading-relaxed">
                      <p className="font-semibold">Hercules House</p>
                      <p className="font-semibold">Merlin Quay</p>
                      <p className="font-semibold">Hazel Road</p>
                      <p className="font-semibold">Southampton</p>
                      <p className="font-semibold">SO19 7GB</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Section - Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 print:gap-2">
                {/* Left Column - Contact Details */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-x-2 gap-y-3 items-center">
                    <div className="font-semibold text-gray-700 text-left">
                      Main Company Phone Number:
                    </div>
                    <div className="text-gray-900 font-medium">
                      {formData.telephone}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      Directors Email address:
                    </div>
                    <div className="text-gray-900 font-medium break-all">
                      {formData.companyEmail}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border rounded overflow-hidden">
              <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
                LINE CONFIGURATION
              </div>

              {/* Combined Table */}
              <table className="w-full border-collapse">
                <thead></thead>
                <tbody>
                  {/* Line Requirements - First Row */}
                  <tr className="bg-white">
                    {[
                      {
                        label: "New / Conversion Isdn2 Channel",
                        value: lineRequirements.newConversionIsdn2Channel,
                      },
                      {
                        label: "Existing ISDN30 Channels",
                        value: lineRequirements.existingIsdn30Channels,
                      },
                      {
                        label: "New ISDN30 Channel",
                        value: lineRequirements.newIsdn30Channel,
                      },
                      {
                        label: "Hosted Lifetime Licenses with SIP Trunk",
                        value: currentLines.hostedLifetimeLicenses,
                      },
                    ].map((item, index) => (
                      <td
                        key={index}
                        className="p-3 border-b border-r align-top"
                      >
                        <div className="font-semibold text-sm text-gray-700 mb-2 min-h-[42px]">
                          {item.label}
                        </div>
                        <div className="w-full border-2 border-gray-300 rounded-md p-3 bg-blue-50 text-lg font-bold text-blue-700 text-center min-h-[48px] flex items-center justify-center">
                          {item.value || "0"}
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Line Requirements - Second Row */}
                  <tr className="bg-gray-50">
                    <td className="p-3 border-b border-r align-top">
                      <div className="font-semibold text-sm text-gray-700 mb-2 min-h-[42px]">
                        Analogue PSTN Lines
                      </div>
                      <div className="w-full border-2 border-gray-300 rounded-md p-3 bg-blue-50 text-lg font-bold text-blue-700 text-center min-h-[48px] flex items-center justify-center">
                        {currentLines.analoguePstnLines || "0"}
                      </div>
                    </td>

                    {/* Data Broadband Requirements */}
                    <td className="p-3 border-b border-r align-top">
                      <div className="font-semibold text-sm text-gray-700 mb-2 min-h-[42px]">
                        Starlink Broadband
                      </div>
                      <div className="w-full border-2 border-gray-300 rounded-md p-3 bg-green-50 text-lg font-bold text-green-700 text-center min-h-[48px] flex items-center justify-center">
                        {dataBroadband.starlinkBroadband || "0"}
                      </div>
                    </td>

                    <td className="p-3 border-b border-r align-top">
                      <div className="font-semibold text-sm text-gray-700 mb-2 min-h-[42px]">
                        Leased Lines
                      </div>
                      <div className="w-full border-2 border-gray-300 rounded-md p-3 bg-green-50 text-lg font-bold text-green-700 text-center min-h-[48px] flex items-center justify-center">
                        {dataBroadband.leasedLines || "0"}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*  EQUIPMENT & SERVICES */}
            <div
              className="border rounded overflow-hidden mt-6"
              style={{ pageBreakBefore: "always" }}
            >
              <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
                EQUIPMENT & SERVICES
              </div>

              {/* Equipment Table */}
              <table className="w-full border-collapse">
                <tbody>
                  {equipmentData.map((row, i) => {
                    // Only for these 3, skip the row if qty is 0
                    const specialItems = ["GRANDSTREAM 3470", "GRANDSTREAM 3480", "CISCO 9871"];
                    if (specialItems.includes(row.equipment) && !row.qty) return null;

                    return (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="p-3 border text-sm text-gray-700">
                          {row.equipment}
                        </td>
                        <td className="p-3 border text-center font-bold text-blue-700 bg-blue-50">
                          {row.qty}
                        </td>
                        <td className="p-3 border text-sm text-gray-700">
                          {row.feature}
                        </td>
                        <td className="p-3 border text-center font-bold text-green-700 bg-green-50">
                          {row.featureQty}
                        </td>
                        <td className="p-3 border text-sm text-gray-700">
                          {row.service}
                        </td>
                        <td className="p-3 border text-center font-bold text-blue-700 bg-blue-50">
                          {row.serviceQty}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Settlement Table */}
              <table className="w-full border border-black border-collapse text-sm">
                <tbody>
                  {/* Row 1 — Network settlement contribution */}
                  <tr>
                    <td className="border border-black align-top p-2 w-2/3">
                      <div>
                        Network settlement contribution to be invoiced to Fibre
                        Telecoms UK Ltd
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-700">The network settlement contribution is paid in a lump sum or can be offered over 6 split payments</div>
                      
                      <div className="mt-2 text-xs text-red-600 font-bold uppercase">
                        NO SETTLEMENTS OR NETWORK SETTLEMENT CONTRIBUTIONS WILL
                        BE PAID WITHOUT AN INVOICE
                      </div>
                    </td>

                    <td className="border border-black text-right font-bold text-blue-700 align-top p-2 whitespace-nowrap">
                      £
                      {(
                        parseInt(leaseCalculatorData.networkPayment || 0, 10) *
                        Math.abs(
                          parseInt(
                            leaseCalculatorData.networkSettlementContribution ||
                              0,
                            10,
                          ),
                        )
                      ).toLocaleString()}
                    </td>

                   <td className="border border-black align-top p-2 w-1/3">
                      <div className="font-semibold text-gray-800">
                        Special Conditions:-
                      </div>
                      <div className="mt-1 text-sm text-gray-700">
                        Existing lease settlements included in this contract will be paid over 6 equal split payments over 6 months.
                      </div>
                      <div className="mt-1 text-sm text-gray-700">
                         We will refund any monthly charges incurred outside of this timeframe
                      </div>

                      {isPdfMode || isViewOnly ? (
                        <div
                          className="w-full border border-gray-300 rounded-md p-2 mt-2 text-sm text-gray-700 whitespace-pre-wrap break-words"
                          style={{ minHeight: "60px" }}
                        >
                          {specialConditions || ""}
                        </div>
                      ) : (
                        <textarea
                          className="w-full border border-gray-300 rounded-md p-2 mt-2 text-sm"
                          placeholder="Additional notes..."
                          value={specialConditions}
                          onChange={(e) => setSpecialConditions(e.target.value)}
                          style={{ resize: "vertical", minHeight: "60px" }}
                        />
                      )}
                    </td>
                  </tr>

                  {/* Row 2 — Settlements */}
                  <tr>
                    <td className="border border-black p-2 text-gray-800">
                      Settlements included for existing contracts (Including
                      Leases)
                    </td>
                    <td className="border border-black text-right font-bold text-blue-700 p-2">
                      £{settlementsTotal.toLocaleString()}
                    </td>
                    <td className="border border-black p-2"></td>
                  </tr>

                  {/* Row 3 — Cashback */}
                  <tr>
                    <td className="border border-black p-2 text-gray-800">
                      Cash back for Mobile Savings
                    </td>
                    <td className="border border-black text-right font-bold text-blue-700 p-2">
                      {isGeneratingPDF || isViewOnly ? ( // Add isViewOnly check here
                        <div className="w-24 text-sm text-center text-gray-800 font-semibold">
                          £{cashback || 0}
                        </div>
                      ) : (
                        <input
                          type="text"
                          className="w-24 border border-gray-300 rounded-md p-1 text-sm text-center"
                          placeholder="£0"
                          value={cashback}
                          onChange={(e) => setCashback(e.target.value)}
                          readOnly={isViewOnly} // Add readOnly prop
                        />
                      )}
                    </td>
                    <td className="border border-black p-2"></td>
                  </tr>
                </tbody>
              </table>

              {/* Notes Section */}
              <div className="p-4 bg-gray-50 border-t text-sm text-gray-700 leading-relaxed">
                <p>
                  I understand that Fibre Telecoms (UK) Ltd will only pay the
                  amounts stated here towards extricating us from our current
                  contracts or leases.
                </p>
                <p className="mt-2">
                  Fibre Web listing license is an opt-out process where the
                  customer is required to opt out of the service if not
                  required.
                </p>
                <p className="mt-2">
                  Any reimbursements due towards termination charges will only
                  be paid once Fibre have taken over lines and calls, and when
                  the system has been formally accepted into service.
                </p>
              </div>
            </div>
            <div className="border p-4 rounded bg-gray-50">
              <div className="space-y-4">
                <p className="text-sm font-medium">
                  I have read, understood and I acknowledge acceptance of the
                  terms and conditions attached to the docusign, a full version
                  of which are available for viewing at
                  www.fibretelecomsuk.co.uk
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name & Position
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.managerName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Company
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.companyName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Signature
                    </label>
                    <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.date}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <PageFooter
              pageNumber={2}
              totalPages={5}
              showInitials={true}
              sectionTitle="INITIALS"
            />
            <p className="mt-2 text-xs text-gray-700">
              BY INITIALLING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
              ACCORDANCE WITH THE NEW EECC OFCOM RULINGREGARDING SMALL
              BUSINESSES AND CONTRACT TERMS.{" "}
            </p>
            <p className="mt-2 mb-2 text-xs text-gray-700">
              THIS ALSO INCLUDES THE 1 PAGE SUMMARY PAGE REQUIRED TO ACCOMPANY
              THIS DOCUMENT.
            </p>
          </div>

          {/* Maintenance form */}
          <div
            className="border border-grey mt-6 font-bold"
            style={{ pageBreakBefore: "always", printColorAdjust: "exact" }}
          >
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center rounded-t-lg uppercase">
              MAINTENANCE FORM
            </div>

            {/* Customer Information Section */}
            <div className="mb-8 bg-white rounded-xl shadow-md p-8 border border-gray-100">
              {/* Top Section - Company Addresses */}
              <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
                {/* Left Column - Client Company */}
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2 uppercase tracking-wide">
                      Client Company
                    </h3>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      Date :{" "}
                      {formData.date
                        ? formatToDDMMYYYY(formData.date)
                        : "Not specified"}
                    </p>
                    <p className="font-bold text-gray-900 text-lg mb-2">
                      {formData.companyName}
                    </p>
                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {formData.fullAddress}
                    </p>
                  </div>
                </div>

                {/* Right Column - Fibre Telecoms */}
                <div className="flex flex-col space-y-6">
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                      Fibre Telecoms(uk)Ltd
                    </h3>
                    <div className="space-y-1 text-gray-800 leading-relaxed">
                      <p className="font-semibold">Hercules House</p>
                      <p className="font-semibold">Merlin Quay</p>
                      <p className="font-semibold">Hazel Road</p>
                      <p className="font-semibold">Southampton</p>
                      <p className="font-semibold">SO19 7GB</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Section - Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 print:gap-2">
                {/* Left Column - Contact Details */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-x-2 gap-y-3 items-center">
                    <div className="font-semibold text-gray-700 text-left">
                      Main Company Phone Number:
                    </div>
                    <div className="text-gray-900 font-medium">
                      {formData.telephone}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      Directors Email address:
                    </div>
                    <div className="text-gray-900 font-medium break-all">
                      {formData.companyEmail}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="border rounded overflow-hidden">
              <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
                LINE CONFIGURATION
              </div>

              {/* Combined Table */}
              <table className="w-full border-collapse">
                <thead></thead>
                <tbody>
                  {/* Line Requirements - First Row */}
                  <tr className="bg-white">
                    {[
                      {
                        label: "New / Conversion Isdn2 Channel",
                        value: lineRequirements.newConversionIsdn2Channel,
                      },
                      {
                        label: "Existing ISDN30 Channels",
                        value: lineRequirements.existingIsdn30Channels,
                      },
                      {
                        label: "Leased Lines",
                        value: dataBroadband.leasedLines,
                      },
                      {
                        label: "Starlink Broadband",
                        value: dataBroadband.starlinkBroadband,
                      },
                    ].map((item, index) => (
                      <td
                        key={index}
                        className="p-3 border-b border-r align-top"
                      >
                        <div className="font-semibold text-sm text-gray-700 mb-2 min-h-[42px]">
                          {item.label}
                        </div>
                        <div className="w-full border-2 border-gray-300 rounded-md p-3 bg-blue-50 text-lg font-bold text-blue-700 text-center min-h-[48px] flex items-center justify-center">
                          {item.value || "0"}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            {/*  EQUIPMENT & SERVICES */}
            <div>
              <div
                className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center"
                style={{ pageBreakBefore: "always" }}
              >
                EQUIPMENT & SERVICES
              </div>

              {/* Equipment Table */}
              <table className="w-full border-collapse">
                <tbody>
                 {equipmentData.map((row, i) => {
                  const specialItems = ["GRANDSTREAM 3470", "GRANDSTREAM 3480", "CISCO 9871"];
                  if (specialItems.includes(row.equipment) && !row.qty) return null;

                  return (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="p-3 border text-sm text-gray-700">
                        {row.equipment}
                      </td>
                      <td className="p-3 border text-center font-bold text-blue-700 bg-blue-50">
                        {row.qty}
                      </td>
                      <td className="p-3 border text-sm text-gray-700">
                        {row.feature}
                      </td>
                      <td className="p-3 border text-center font-bold text-green-700 bg-green-50">
                        {row.featureQty}
                      </td>
                      <td className="p-3 border text-sm text-gray-700">
                        {row.service}
                      </td>
                      <td className="p-3 border text-center font-bold text-blue-700 bg-blue-50">
                        {row.serviceQty}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>

              {/* Settlement Table */}
              <table className="w-full border-collapse mt-4">
                <tbody>
                  <tr className="bg-white">
                    <td className="p-3 border text-sm text-gray-700 w-2/3">
                      Number of months included free in system package
                    </td>
                    <td className="p-3 border text-right font-bold text-blue-700">
                      6
                    </td>
                    <td className="p-3 border"></td>
                  </tr>

                  <tr className="bg-gray-50">
                    <td className="p-3 border text-sm text-gray-700">
                      Service Level
                    </td>
                    <td className="p-3 border text-center font-bold text-blue-700">
                      1
                    </td>
                    <td className="p-3 border text-sm text-gray-600">
                      Monday to Friday 9–5:30 availability excluding bank
                      holidays
                    </td>
                  </tr>

                  <tr className="bg-white">
                    <td
                      colSpan="3"
                      className="p-3 border text-sm text-gray-800"
                    >
                      After the inclusive period of warranty the cost per month
                      for the above equipment will be:
                      <div className="mt-2">
                        <div className="flex justify-between">
                          <span>
                            £{(mobileAppUsers_noFoc * 13.25).toFixed(2)} Ex.VAT
                            (Service Level 1)
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>
                            £{(mobileAppUsers_noFoc * 17.25).toFixed(2)} Ex.VAT
                            (Service Level 3)
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <tr className="bg-gray-50">
                    <td
                      colSpan="3"
                      className="p-3 border text-sm text-gray-800 font-semibold"
                    >
                      In the event of any remote maintenance work performed
                      outwith the service timescales above will be charged at a
                      flat rate of £54 + VAT
                    </td>
                  </tr>

                  <tr className="bg-white">
                    <td
                      colSpan="3"
                      className="p-3 border text-sm text-gray-700"
                    >
                      The term of the maintenance agreement is{" "}
                      <strong>
                        {leaseCalculatorData.paymentProfile} months
                      </strong>
                    </td>
                  </tr>

                  <tr className="bg-gray-50">
                    <td
                      colSpan="3"
                      className="p-3 border text-sm text-gray-700"
                    >
                      CALL OUT CHARGES OUTWITH MAINTENANCE TIMESCALES IS = £250
                      + VAT (HALF DAY), £500 + VAT (FULL DAY)
                    </td>
                  </tr>

                  <tr className="bg-white">
                    <td
                      colSpan="3"
                      className="p-3 border text-sm text-gray-600 italic"
                    >
                      I have read, understood and I acknowledge acceptance of
                      the terms and conditions attached to the docusign, a full
                      version of which are available for viewing at
                      <a
                        href="http://www.fibretelecomsuk.co.uk"
                        className="text-blue-600 underline ml-1"
                      >
                        www.fibretelecomsuk.co.uk
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Notes Section */}
              <div className="p-4 bg-gray-50 border-t text-sm text-gray-700 leading-relaxed">
                <p>
                  I understand that Fibre Telecoms (UK) Ltd will only pay the
                  amounts stated here towards extricating us from our current
                  contracts or leases.
                </p>
                <p className="mt-2">
                  Fibre Web listing license is an opt-out process where the
                  customer is required to opt out of the service if not
                  required.
                </p>
                <p className="mt-2">
                  Any reimbursements due towards termination charges will only
                  be paid once Fibre have taken over lines and calls, and when
                  the system has been formally accepted into service.
                </p>
              </div>
            </div>
            <div className="border p-4 rounded bg-gray-50">
              <div className="space-y-4">
                <p className="text-sm font-medium">
                  I have read, understood and I acknowledge acceptance of the
                  terms and conditions attached to the docusign, a full version
                  of which are available for viewing at
                  www.fibretelecomsuk.co.uk
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name & Position
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.managerName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Company
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.companyName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Signature
                    </label>
                    <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.date}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PageFooter
              pageNumber={3}
              totalPages={5}
              showInitials={true}
              sectionTitle="INITIALS"
            />
            <p className="mt-2 text-xs text-gray-700">
              BY INITIALLING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
              ACCORDANCE WITH THE NEW EECC OFCOM RULINGREGARDING SMALL
              BUSINESSES AND CONTRACT TERMS.{" "}
            </p>
            <p className="mt-2 mb-2 text-xs text-gray-700">
              THIS ALSO INCLUDES THE 1 PAGE SUMMARY PAGE REQUIRED TO ACCOMPANY
              THIS DOCUMENT.
            </p>
          </div>

          {/* LINE RENTAL & BROADBAND */}
          <div
            className="border border-gray-300 mt-6 font-bold"
            style={{ printColorAdjust: "exact" }}
          >
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center uppercase">
              LINE RENTAL & BROADBAND
            </div>

            {/* Customer Information Section */}
            <div className="mb-6 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              {/* Top Section - Company Addresses */}
              <div className="grid grid-cols-2 gap-6 mb-6 pb-4 border-b border-gray-200">
                {/* Left Column - Client Company */}
                <div className="space-y-2">
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <h3 className="text-xs font-semibold text-blue-800 mb-1 uppercase tracking-wide">
                      Client Company
                    </h3>
                    <p className="font-bold text-gray-900 text-base mb-1">
                      Date :{" "}
                      {formData.date
                        ? formatToDDMMYYYY(formData.date)
                        : "Not specified"}
                    </p>
                    <p className="font-bold text-gray-900 text-base mb-1">
                      {formData.companyName}
                    </p>
                    <p className="text-gray-700 text-sm whitespace-pre-line leading-tight">
                      {formData.fullAddress}
                    </p>
                  </div>
                </div>

                {/* Right Column - Fibre Telecoms */}
                <div className="flex flex-col space-y-4">
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Fibre Telecoms Ltd
                    </h3>
                    <div className="space-y-0 text-gray-800 text-sm leading-tight">
                      <p className="font-semibold">Hercules House</p>
                      <p className="font-semibold">Merlin Quay</p>
                      <p className="font-semibold">Hazel Road</p>
                      <p className="font-semibold">Southampton</p>
                      <p className="font-semibold">SO19 7GB</p>
                      <p className="mt-2 font-medium">Mobile: 0330 094 8797</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Section - Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column - Contact Details */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 items-center text-sm">
                    <div className="font-semibold text-gray-700 text-left">
                      Main Company Phone Number:
                    </div>
                    <div className="text-gray-900 font-medium">
                      {formData.telephone}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      Directors Email address:
                    </div>
                    <div className="text-blue-600 font-medium bg-blue-50 p-1 rounded text-xs break-all">
                      {formData.companyEmail}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      Email address for billing:
                    </div>
                    <div className="text-blue-600 font-medium bg-blue-50 p-1 rounded text-xs break-all">
                      {formData.billingEmail}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      24 hr Service:
                    </div>
                    <div>
                      {isGeneratingPDF || isViewOnly ? ( // Add isViewOnly check
                        <div className="w-full text-xs font-medium text-gray-800">
                          {formData.twentyFourHourService &&
                          formData.twentyFourHourService.toLowerCase() === "no"
                            ? "No"
                            : "Yes"}
                        </div>
                      ) : (
                        <select
                          value={formData.twentyFourHourService || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "twentyFourHourService",
                              e.target.value,
                            )
                          }
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          disabled={isViewOnly} // Optional: disable instead of hiding
                        >
                          <option value="No">No</option>
                          <option value="Yes">Yes</option>
                        </select>
                      )}
                    </div>

                    <div className="font-semibold text-gray-700 text-left">
                      Agreement Term
                    </div>
                    <div className="text-blue-600 text-sm">
                      {leaseCalculatorData.paymentProfile
                        ? `${leaseCalculatorData.paymentProfile / 12} years`
                        : ""}
                    </div>
                  </div>
                </div>

                {/* Right Column - Company Details */}
                <div className="flex flex-col space-y-4">
                  <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                      Company Details
                    </h3>
                    <div className="space-y-1 text-gray-800 text-sm leading-tight">
                      <p>
                        <span className="font-semibold">
                          VAT Registration No:
                        </span>{" "}
                        307 5944 89
                      </p>
                      <p>
                        <span className="font-semibold">
                          Registered Office:
                        </span>{" "}
                        Kingfisher House, Hampshire, SO52 9LP
                      </p>
                      <p>
                        <span className="font-semibold">
                          Registered in England No:
                        </span>{" "}
                        11276097
                      </p>
                      <p>
                        <span className="font-semibold">Contact number:</span>{" "}
                        0330 094 8797
                      </p>
                    </div>
                  </div>

                  {/* Tariff */}
                  <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                    <p className="text-blue-700 font-semibold text-xs">
                      Tariff:{" "}
                      <span className="font-medium">FIBRE SIP BUNDLE</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Selection Sections */}
            <div className="mb-4 bg-gray-50 border border-gray-200 rounded-md p-4">
              {/* Fibre Undertaking */}
              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-800 mb-1">
                  In return for the agreed term of service, Fibre undertakes to:
                </h3>
                <p className="text-xs text-gray-700 leading-tight">
                  Route all telephone numbers and/or lines relating to the above
                  address via the Fibre network, at the agreed rates. (
                  <span className="text-blue-600">
                    Please see accompanying tariff sheet
                  </span>
                  )
                </p>
                <p className="text-xs text-gray-700 leading-tight mt-1">
                  Provide Internet based call management reporting tools free of
                  charge.
                </p>
              </div>

              {/* Customer Obligations */}
              <div>
                <h3 className="text-xs font-semibold text-gray-800 mb-2">
                  In return for the above, the Customer agrees to:
                </h3>
                <ul className="list-disc list-inside space-y-1 text-xs text-gray-700 leading-tight">
                  <li>
                    Arrange access to the Customers PABX in order to
                    re-programme for the purpose of providing Least Cost
                    Routing, via the system or to authorise the application of
                    CPS for the same.
                  </li>
                  <li>
                    Pay all call charges on time by direct debit upon receipt of
                    a valid invoice.
                  </li>
                  <li>
                    Repay in full any termination charges paid by us on behalf
                    of the Customer to a previous suppliers should the Customer
                    wish to end this Agreement at any time prior to the agreed
                    term
                  </li>
                  <li>Authorise registration of TPS by us</li>
                </ul>
              </div>
            </div>

            <div className="grid gap-4 mb-4">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 border font-semibold text-gray-700 text-center">
                      Least Cost Routing Type
                    </th>
                    <th className="p-2 border font-semibold text-gray-700 text-center">
                      CPS
                    </th>
                    <th className="p-2 border font-semibold text-gray-700 text-center">
                      PABX
                    </th>
                    <th className="p-2 border font-semibold text-gray-700 text-center">
                      Re-Programming Existing PABX
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white">
                    <td className="p-2 border font-semibold text-gray-800 text-center">
                      Available
                    </td>
                    <td className="p-2 border text-gray-700 text-center">
                      Yes
                    </td>
                    <td className="p-2 border text-gray-700 text-center">
                      Yes
                    </td>
                    <td className="p-2 border text-gray-700 text-center">
                      Yes
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Service Details Table */}
            <div className="print-container">
              <div className="grid grid-cols-1 print:grid-cols-2 gap-4">
                {/* Left Table - Service Selection */}
                <div className="bg-white rounded-md shadow-sm overflow-hidden print:border print:border-gray-300">
                  <div className="bg-blue-600 text-white px-3 py-2">
                    <h3 className="text-sm font-semibold">Current Numbers</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-2 py-1 text-left font-medium text-gray-700 uppercase border-b">
                            Telephone
                          </th>
                          <th className="px-2 py-1 text-left font-medium text-gray-700 uppercase border-b">
                            Postcode if homeworker
                          </th>
                          <th className="px-2 py-1 text-left font-medium text-gray-700 uppercase border-b">
                            No of Lines
                          </th>
                          <th className="px-2 py-1 text-left font-medium text-gray-700 uppercase border-b">
                            Type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {telephoneLines && telephoneLines.length > 0 ? (
                          telephoneLines.map((line, idx) => (
                            <tr
                              key={idx}
                              className={
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-2 py-1 whitespace-nowrap font-medium text-gray-900">
                                {line.existing_tel_no || "N/A"}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-gray-700">
                                {line.postcode || "N/A"}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-gray-700">
                                {line.quantity || "N/A"}
                              </td>
                              <td className="px-2 py-1 whitespace-nowrap text-gray-700">
                                {line.description || "N/A"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className="bg-gray-50">
                            <td
                              colSpan="4"
                              className="px-2 py-3 text-center text-gray-500 italic text-xs"
                            >
                              No telephone line details added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Right Table - Service Details */}
                <div className="bg-white rounded-md shadow-sm overflow-hidden print:border print:border-gray-300">
                  <div className="bg-blue-600 text-white px-3 py-2">
                    <h3 className="text-sm font-semibold">Contract Details</h3>
                  </div>
                  <table className="w-full border-collapse text-xs">
                    <tbody>
                      <tr className="bg-white">
                        <td className="p-2 border text-gray-700">
                          Amount of PSTN / ISDN lines services
                        </td>
                        <td className="p-2 border text-gray-700 text-center">
                          {currentLines.analoguePstnLines}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="p-2 border text-gray-700">
                          Amount of Broadband services
                        </td>
                        <td className="p-2 border text-gray-700 text-center">
                          {broadbandService}
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="p-2 border text-gray-700">
                          Amount of 4G / 5G broadband services
                        </td>
                        <td className="p-2 border text-gray-700 text-center">
                          {new4GQty}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="p-2 border text-gray-700">
                          Direct Debit billing
                        </td>
                        <td className="p-2 border text-gray-700 text-center">
                          Yes
                        </td>
                      </tr>
                      <tr className="bg-white font-semibold">
                        <td className="p-2 border text-gray-800">
                          Monthly cost of broadband services (excl VAT)
                        </td>
                        <td className="p-2 border text-gray-700 text-center">
                          £{broadbandServiceTotal?.rate}
                        </td>
                      </tr>
                      <tr className="bg-gray-100">
                        <td colSpan="2" className="p-2 border text-gray-700">
                          <div className="font-semibold mb-1">
                            Billing and Payments:- Please note.
                          </div>
                          <p className="text-xs text-gray-600">
                            If during the life of the contract the customer
                            changes payment or billing method then the
                            applicable charges will be added to subsequent
                            invoices.
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-4 p-4 bg-white rounded-md shadow-sm">
              <p className="bg-blue-600 text-white text-base font-semibold px-3 py-1 rounded-t-md">
                Broadband
              </p>
              <div className="mt-2 text-xs text-gray-700 leading-tight space-y-2">
                <p>
                  "Fibre are required under Ofcom regulations to create a record
                  of our customers' consent to enter into a contract for
                  Broadband Services. By signing this agreement you hereby give
                  authorisation to Fibre Telecoms (uk) Ltd, to migrate current
                  Broadband Services and/or provide new Broadband Services".
                </p>
                <p>
                  All Broadband Services are subject to Fibre's Acceptable Use
                  Policy, details of which are available upon our website at{" "}
                  <a
                    href="https://www.fibretelecomsuk.co.uk"
                    className="text-blue-600 hover:underline"
                  >
                    www.fibretelecomsuk.co.uk
                  </a>
                </p>
                <p>
                  I have read, understood and I acknowledge acceptance of the
                  terms and conditions attached to the docusign, a full version
                  of which are available for viewing at{" "}
                  <a
                    href="https://www.fibretelecomsuk.co.uk"
                    className="text-blue-600 hover:underline"
                  >
                    www.fibretelecomsuk.co.uk
                  </a>
                </p>
              </div>
            </div>

            {/* Signatures */}
            <div className="border p-3 rounded bg-gray-50">
              <h2 className="text-base font-semibold mb-2">
                SIGNATURES & AUTHORIZATION
              </h2>
              <div className="space-y-3">
                <p className="text-xs font-medium">
                  BY SIGNING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
                  ACCORDANCE WITH THE OFCOM RULING REGARDING SMALL BUSINESSES
                  SUMMARY PAGE AND CONTRACT TERM.
                </p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block font-medium mb-1">
                      Name & Position
                    </label>
                    <div className="w-full border border-gray-300 rounded px-2 py-1 bg-gray-100">
                      {formData.managerName}
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Company</label>
                    <div className="w-full border border-gray-300 rounded px-2 py-1 bg-gray-100">
                      {formData.companyName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="block font-medium mb-1">Signature</label>
                    <div className="w-full h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white text-xs">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Date</label>
                    <div className="w-full border border-gray-300 rounded px-2 py-1 bg-gray-100">
                      {formData.date}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PageFooter
              pageNumber={4}
              totalPages={5}
              showInitials={true}
              sectionTitle="INITIALS"
            />
            <p className="mt-2 text-xs text-gray-700">
              BY INITIALLING THIS DOCUMENT YOU AGREE TO WAIVE YOUR RIGHTS IN
              ACCORDANCE WITH THE NEW EECC OFCOM RULINGREGARDING SMALL
              BUSINESSES AND CONTRACT TERMS.{" "}
            </p>
            <p className="mt-2 mb-2 text-xs text-gray-700">
              THIS ALSO INCLUDES THE 1 PAGE SUMMARY PAGE REQUIRED TO ACCOMPANY
              THIS DOCUMENT.
            </p>
          </div>

          {/* Customer Requirements */}
          {/* CUSTOMER REQUIREMENTS */}

          <div
            className="border border-gray-300 mt-6 font-bold"
            style={{ printColorAdjust: "exact" }}
          >
            {/* Section Header */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-4 py-3 font-bold text-lg text-center">
              CUSTOMER REQUIREMENTS
            </div>

            {/* Section Body */}
            <div className="border border-t-0 p-6 bg-white">
              <div className="grid md:grid-cols-2 gap-6">
                {customerRequirements.map((item, index) => (
                  <label
                    key={index}
                    className={`flex items-start space-x-3 text-sm text-gray-700 leading-relaxed ${isViewOnly ? "opacity-90" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData[item.key]}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [item.key]: e.target.checked,
                        })
                      }
                      className={`h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500 ${isViewOnly ? "cursor-not-allowed opacity-70" : ""}`}
                      disabled={isViewOnly} // Disable in view mode
                    />
                    <span className={isViewOnly ? "select-none" : ""}>
                      {item.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div
              className="mt-6 rounded-md shadow-md overflow-hidden"
              style={{ printColorAdjust: "exact" }}
            >
              {/* Section Header */}
              <div className="bg-blue-600 text-white text-lg font-semibold px-4 py-2">
                DOCUMENTS PROVIDED
              </div>

              {/* Section Body */}
              <div className="border border-t-0 p-6 bg-white">
                <p className="mb-4 text-gray-700 text-sm">
                  I have been given copies of the following documents (tick as
                  appropriate):
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-3">
                    {documentsListLeft.map((doc) => (
                      <label
                        key={doc.key}
                        className={`flex items-center space-x-2 text-sm ${isViewOnly ? "text-gray-600" : "text-gray-700"}`}
                      >
                        <input
                          type="checkbox"
                          checked={formData[doc.key]}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              [doc.key]: e.target.checked,
                            }))
                          }
                          className={`h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500 ${isViewOnly ? "cursor-not-allowed opacity-70" : ""}`}
                          disabled={isViewOnly}
                        />
                        <span className={isViewOnly ? "select-none" : ""}>
                          {doc.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3">
                    {documentsListRight.map((doc) => (
                      <label
                        key={doc.key}
                        className={`flex items-center space-x-2 text-sm ${isViewOnly ? "text-gray-600" : "text-gray-700"}`}
                      >
                        <input
                          type="checkbox"
                          checked={formData[doc.key]}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              [doc.key]: e.target.checked,
                            }))
                          }
                          className={`h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500 ${isViewOnly ? "cursor-not-allowed opacity-70" : ""}`}
                          disabled={isViewOnly}
                        />
                        <span className={isViewOnly ? "select-none" : ""}>
                          {doc.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Declaration */}
                <p className="mt-6 text-sm text-gray-700 leading-relaxed">
                  I declare that, to the best of my knowledge, the above
                  statements are true and I have been presented with pages 1 to
                  5 of the Fibre Telecoms UK Ltd contract pack.
                </p>
              </div>
            </div>
            <div className="border p-4 rounded bg-gray-50">
              <h2 className="text-lg font-semibold mb-4">
                SIGNATURES & AUTHORIZATION
              </h2>
              <div className="space-y-4">
                <p className="text-sm font-medium">
                  I declare that, to the best of my knowledge, the above
                  statements are true and I have been presented with pages 1 to
                  5 of the Fibre Telecoms UK Ltd contract pack.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Name & Position
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.managerName}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Company
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.companyName}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Signature
                    </label>
                    <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                      {"{{Sig1_es_:signer1:signature}}"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date
                    </label>
                    <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                      {formData.date}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center text-[10px] text-gray-700 mt-2 mb-2">
              <span>
                We confirm that any financial information given will be treated
                with utmost confidentiality.
              </span>
              <span className="whitespace-nowrap">
                Date: {formData.date ? formatToDDMMYYYY(formData.date) : ""}
              </span>
            </div>
            <PageFooter
              pageNumber={5}
              totalPages={5}
              showInitials={true}
              sectionTitle="INITIALS"
            />
            <p className="mt-2 mb-2 text-xs text-gray-700">
              BY INITIALLING AND SIGNING PAGES 1 TO 5 OF THIS DOCUMENT YOU AGREE
              TO WAIVE YOUR RIGHTS TO THE SUMMARY DOCUMENT AND MAXIMUM 24 MONTH
              CONTRACT LENGTH FOR SMALL BUSINESSES
            </p>
          </div>

          {/* MOBILES */}
          {mobileData && Object.keys(mobileData).length > 0 && (
            <div
              className="border border-grey mt-6 font-bold"
              style={{ printColorAdjust: "exact" }}
            >
              <div
                className="bg-blue-600 text-white text-center text-xl font-bold px-4 py-2 rounded-t-md"
                style={{ pageBreakBefore: "always" }}
              >
                MOBILES
              </div>
              <div className="bg-white rounded-2xl shadow-lg w-full p-6">
                {/* Header */}

                <div className="mb-8 bg-white rounded-xl shadow-md p-8 border border-gray-100">
                  {/* Top Section - Company Addresses */}
                  <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
                    {/* Left Column - Client Company */}
                    <div className="space-y-3">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <p className="font-bold text-gray-900 text-lg mb-2">
                          Date :{" "}
                          {formData.date
                            ? formatToDDMMYYYY(formData.date)
                            : "Not specified"}
                        </p>
                        <p className="font-bold text-gray-900 text-lg mb-2">
                          {formData.companyName}
                        </p>
                        <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                          {formData.fullAddress}
                        </p>
                      </div>
                    </div>

                    {/* Right Column - Fibre Telecoms */}
                    <div className="flex flex-col space-y-6">
                      <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 shadow-sm">
                        <div className="space-y-1 text-gray-800 leading-relaxed">
                          <p className="font-semibold">
                            Fibre Telecoms (UK) Ltd
                          </p>
                          <p className="font-semibold">
                            Unit 71, Basepoint Business Centre
                          </p>
                          <p className="font-semibold">Andersons Road</p>
                          <p className="font-semibold">Southampton</p>
                          <p className="font-semibold">SO14 5FE</p>
                          <p className="mt-3 font-medium">
                            Mobile: 0330 094 8797
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1  gap-6 print:gap-4">
                    {/* Left Column - Contact Details */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-6 items-center">
                        <div className="font-semibold text-gray-700 text-left pr-4">
                          Main Company Phone Number:
                        </div>
                        <div className="text-gray-900 font-medium">
                          {formData.telephone}
                        </div>

                        <div className="font-semibold text-gray-700 text-left pr-4">
                          Directors Email address:
                        </div>
                        <div className="text-blue-600 font-medium bg-blue-50 p-2 rounded break-all print:bg-white print:border print:border-gray-300 print:text-xs">
                          {formData.companyEmail}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SIM Tariff */}
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  📶 SIM Tariff
                </h2>
                <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200 mb-6">
                  <table className="w-full text-center">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-lg">
                        <th className="p-3">Unlimited</th>
                        <th className="p-3">40 GB</th>
                        <th className="p-3">20 GB</th>
                        <th className="p-3">10 GB</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-800 text-lg font-medium">
                      <tr className="odd:bg-gray-50 even:bg-white">
                        <td className="p-4 border">
                          {getDataCount("unlimited")}
                        </td>
                        <td className="p-4 border">{getDataCount("40 GB")}</td>
                        <td className="p-4 border">{getDataCount("20 GB")}</td>
                        <td className="p-4 border">{getDataCount("10 GB")}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Contract Phones */}

                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  📱 Contract Phones
                </h2>
                <div className="overflow-x-auto rounded-2xl shadow-lg border border-gray-200 mb-6">
                  <table className="w-full text-center border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-green-500 to-green-700 text-white text-lg">
                        <th className="p-3 border">
                          Standard
                          <br />
                          <span className="text-sm font-medium">Level</span>
                        </th>
                        <th className="p-3 border">
                          Manager
                          <br />
                          <span className="text-sm font-medium">Level</span>
                        </th>
                        <th className="p-3 border">
                          Director
                          <br />
                          <span className="text-sm font-medium">Level</span>
                        </th>
                        <th className="p-3 border">
                          Director Premium
                          <br />
                          <span className="text-sm font-medium">Level</span>
                        </th>
                      </tr>
                      <tr className="bg-gray-100 text-gray-700 text-sm font-semibold">
                        <th className="p-2 border">
                          Contract Phone
                          <br />
                          Quantity
                        </th>
                        <th className="p-2 border">
                          Contract Phone
                          <br />
                          Quantity
                        </th>
                        <th className="p-2 border">
                          Contract Phone
                          <br />
                          Quantity
                        </th>
                        <th className="p-2 border">
                          Contract Phone
                          <br />
                          Quantity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-900 text-xl font-bold">
                      <tr className="odd:bg-white even:bg-gray-50">
                        <td className="p-4 border">
                          {getStaffCount("standard")}
                        </td>
                        <td className="p-4 border">
                          {getStaffCount("manager")}
                        </td>
                        <td className="p-4 border">
                          {getStaffCount("director")}
                        </td>
                        <td className="p-4 border">
                          {getStaffCount("director_premium")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Warranty Statement */}
                <div className="mt-8 p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
                  <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">
                    🛡️ Warranty Statement
                  </h2>
                  <p className="mb-6 text-gray-700 leading-relaxed text-sm">
                    All Hardware provided by Fibre is covered by Manufacturer's
                    Warranty. These Manufacturer's Warranties vary, and Fibre
                    advises you to read the specific terms and conditions of the
                    Manufacturer's Warranty found in the enclosed Warranty Card
                    or online Warranty Guide at the Manufacturer's website upon
                    receipt of the phone. If a Warranty claim is made, Fibre
                    will help guide you to a Recommended Authorised Service
                    Company for repair. All additional cost is not the
                    responsibility of Fibre.
                  </p>

                  {/* Contract Terms */}
                  <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">
                    📜 Contract Terms
                  </h2>

                  {/* First Block */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* Sims Block */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-lg font-semibold p-3">
                        Sims
                      </div>
                      <div className="p-4 space-y-3">
                        <div className="flex justify-between">
                          <p className="text-sm font-semibold text-gray-600">
                            Existing Connections
                          </p>
                          <p className="text-lg font-bold text-gray-800">
                            {mobileData?.existing_sims_to_migrate ?? 0}
                          </p>
                        </div>
                        <div className="flex justify-between">
                          <p className="text-sm font-semibold text-gray-600">
                            New Connections
                          </p>
                          <p className="text-lg font-bold text-gray-800">
                            {mobileData?.new_sim_cards_to_provide ?? 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Months to End of Current Contract */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-lg font-semibold p-3">
                        Months to End of Current Contract
                      </div>
                      <div className="p-4 text-center text-gray-800 font-bold">
                        0
                      </div>
                    </div>

                    {/* Existing Provider */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-lg font-semibold p-3">
                        Existing Provider
                      </div>
                      <div className="p-4 text-center text-gray-800 font-bold">
                        {mobileData?.current_provider ?? "Vodafone"}
                      </div>
                    </div>
                  </div>

                  {/* Second Block */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* New Provider */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-700 text-white text-lg font-semibold p-3">
                        New Provider
                      </div>
                      <div className="p-4 text-center text-gray-800 font-bold">
                        {mobileData?.new_provider ?? "Vodafone"}
                      </div>
                    </div>

                    {/* Minimum Term */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-700 text-white text-lg font-semibold p-3">
                        Minimum Term (months)
                      </div>
                      <div className="p-4 text-center text-gray-800 font-bold">
                        24
                      </div>
                    </div>

                    {/* Start Date */}
                    <div className="rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-green-500 to-green-700 text-white text-lg font-semibold p-3">
                        * Start Date
                      </div>
                      <div className="p-4 text-center text-gray-800 font-bold">
                        {formatToDDMMYYYY(mobileData?.contract_end_date)}
                      </div>
                    </div>
                  </div>

                  {/* Optional footnote */}
                  <p className="text-sm text-gray-600 mt-2 italic">
                    *Please note the start date will be on this date or on
                    delivery of mobile network service
                  </p>
                </div>
              </div>

              <div
                className="border p-4 rounded"
                style={{ pageBreakBefore: "always", printColorAdjust: "exact" }}
              >
                <h2 className="text-lg font-semibold mb-4"></h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {mobileRequirements.map((item, index) => (
                    <label
                      key={index}
                      className="flex items-start space-x-3 text-sm text-gray-700 leading-relaxed"
                    >
                      <input
                        type="checkbox"
                        checked={formData[item.key]}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            [item.key]: e.target.checked,
                          })
                        }
                        className="h-4 w-4 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                      />
                      <span>{item.text}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Signature Section */}
              <div className="border p-4 rounded bg-gray-50">
                <h2 className="text-lg font-semibold mb-4">
                  SIGNATURES & AUTHORIZATION
                </h2>
                <div className="space-y-4">
                  <p className="text-sm font-medium">
                    I have read, understood and I acknowledge acceptance of the
                    terms and conditions overleaf, a full version of which are
                    available for viewing at www.fibretelecomsuk.co.uk
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Name & Position
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.managerName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Company
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.companyName}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Signature
                      </label>
                      <div className="adobe-sig w-full h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white">
                        {"{{Sig1_es_:signer1:signature}}"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Date
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100">
                        {formData.date}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DIGITAL SERVICES ADDENDUM */}
          {hasDigitalServices && (
            <div
              className="border border-gray-300 mt-6"
              style={{ pageBreakBefore: "always", printColorAdjust: "exact" }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white px-6 py-4 font-bold text-xl text-center">
                DIGITAL SERVICES ADDENDUM
              </div>
              <div className="bg-white p-6 text-sm text-gray-700 leading-relaxed space-y-4">
                <p className="italic text-xs text-gray-500">
                  Addendum to Fibre Telecoms Customer Agreement
                </p>
                <p>
                  This Digital Services Addendum forms part of the Customer's
                  agreement with Fibre Telecoms and covers the provision of
                  digital platform services delivered by Talk Nerdy To Me
                  Consultancy Limited ("TNTM").
                </p>

                {/* Service Provider */}
                <div>
                  <h3 className="font-bold text-gray-900 uppercase mb-1">
                    Service Provider
                  </h3>
                  <p>Talk Nerdy To Me Consultancy Limited</p>
                  <p>9 Mistral, 32 Channel Way</p>
                  <p>Southampton, SO14 3JA</p>
                  <p>Company Number: 13303937</p>
                </div>

                {/* Customer */}
                <div>
                  <h3 className="font-bold text-gray-900 uppercase mb-1">
                    Customer
                  </h3>
                  <p>{formData.companyName}</p>
                  <p className="whitespace-pre-line">{formData.fullAddress}</p>
                </div>

                <p>
                  The Customer may subscribe to one or more of the following
                  digital platform services. Charges will appear within the
                  Fibre Telecoms billing agreement under Digital Service
                  Charges.
                </p>

                {/* WIZZYL */}
                {digitalServices.licenceServices?.Wizzyl > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-600 text-white px-4 py-2 font-bold uppercase">
                      Wizzyl — Website Platform
                    </div>
                    <div className="p-4 space-y-2">
                      <p>
                        Wizzyl provides a professionally built website platform
                        for businesses. The website will be built for the
                        Customer with no upfront build charge. The platform
                        service includes:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                        <li>Website hosting</li>
                        <li>Website CMS access for users</li>
                        <li>Back-ups, security and maintenance</li>
                        <li>Ongoing support</li>
                        <li>Built-in web app functionality</li>
                        <li>AI technology for on-site SEO</li>
                      </ul>
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between font-bold">
                          <span>Monthly platform licence fee</span>
                          <span>
                            £
                            {(
                              digitalServices.licenceServices.Wizzyl * 49.99
                            ).toFixed(2)}{" "}
                            / month + VAT
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-600 mt-1">
                          <span>Active licences</span>
                          <span>
                            {digitalServices.licenceServices.Wizzyl} licence
                            {digitalServices.licenceServices.Wizzyl > 1
                              ? "s"
                              : ""}{" "}
                            × £49.99
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 6ELA */}
                {digitalServices.licenceServices?.["6ELA"] > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-600 text-white px-4 py-2 font-bold uppercase">
                      6ELA — Invoice &amp; Credit Control Platform
                    </div>
                    <div className="p-4 space-y-2">
                      <p>
                        6ELA is a software platform designed to help businesses
                        manage invoices, recover unpaid payments and improve
                        cashflow visibility.
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                        <li>Invoice tracking and payment monitoring</li>
                        <li>Automated payment reminders</li>
                        <li>Credit control workflows</li>
                        <li>Pre-LBA debt chasing tools</li>
                        <li>AI-powered insights into payment behaviour</li>
                        <li>
                          Instant connection to solicitors for legal escalation
                        </li>
                      </ul>
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between font-bold">
                          <span>Licence fee</span>
                          <span>
                            £
                            {(
                              digitalServices.licenceServices["6ELA"] * 49.99
                            ).toFixed(2)}{" "}
                            / month + VAT
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-600 mt-1">
                          <span>Active user licences</span>
                          <span>
                            {digitalServices.licenceServices["6ELA"]} licence
                            {digitalServices.licenceServices["6ELA"] > 1
                              ? "s"
                              : ""}{" "}
                            × £49.99 / user / month
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* AXO SIGNAL */}
                {digitalServices.axoSignalPackage &&
                  Object.keys(digitalServices.axoSignalPackage).length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-600 text-white px-4 py-2 font-bold uppercase">
                        AXO Signal — Market Intelligence Platform
                      </div>
                      <div className="p-4 space-y-2">
                        <p>
                          AXO Signal is a business intelligence platform that
                          identifies companies entering the market and provides
                          enriched company data.
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                          <li>Company details &amp; director information</li>
                          <li>Email addresses &amp; telephone numbers</li>
                          <li>Social profile links</li>
                        </ul>
                        <div className="border-t pt-2 mt-2 space-y-1">
                          <div className="flex justify-between font-bold">
                            <span>Package</span>
                            <span>
                              {digitalServices.axoSignalPackage.label ??
                                "AXO Launch"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Companies per month</span>
                            <span>
                              {digitalServices.axoSignalPackage
                                .companiesPerMonth != null
                                ? digitalServices.axoSignalPackage.companiesPerMonth.toLocaleString()
                                : "Unlimited"}
                            </span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>Monthly cost</span>
                            <span>
                              £
                              {digitalServices.axoSignalPackage.price?.toFixed(
                                2,
                              ) ?? "250.00"}{" "}
                              + VAT
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Cost Summary */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 font-bold uppercase text-gray-800">
                    Digital Services Cost Summary
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {digitalServices.licenceServices?.Wizzyl > 0 && (
                        <tr className="border-b">
                          <td className="px-4 py-2">
                            Wizzyl ({digitalServices.licenceServices.Wizzyl}{" "}
                            licence
                            {digitalServices.licenceServices.Wizzyl > 1
                              ? "s"
                              : ""}
                            )
                          </td>
                          <td className="px-4 py-2 text-right">
                            £
                            {(
                              digitalServices.licenceServices.Wizzyl * 49.99
                            ).toFixed(2)}{" "}
                            + VAT
                          </td>
                        </tr>
                      )}
                      {digitalServices.licenceServices?.["6ELA"] > 0 && (
                        <tr className="border-b">
                          <td className="px-4 py-2">
                            6ELA ({digitalServices.licenceServices["6ELA"]}{" "}
                            licence
                            {digitalServices.licenceServices["6ELA"] > 1
                              ? "s"
                              : ""}
                            )
                          </td>
                          <td className="px-4 py-2 text-right">
                            £
                            {(
                              digitalServices.licenceServices["6ELA"] * 49.99
                            ).toFixed(2)}{" "}
                            + VAT
                          </td>
                        </tr>
                      )}
                      {digitalServices.axoSignalPackage &&
                        Object.keys(digitalServices.axoSignalPackage).length >
                          0 && (
                          <tr className="border-b">
                            <td className="px-4 py-2">
                              AXO Signal (
                              {digitalServices.axoSignalPackage.label ??
                                "AXO Launch"}
                              )
                            </td>
                            <td className="px-4 py-2 text-right">
                              £
                              {digitalServices.axoSignalPackage.price?.toFixed(
                                2,
                              ) ?? "250.00"}{" "}
                              + VAT
                            </td>
                          </tr>
                        )}
                      <tr className="border-b bg-gray-50">
                        <td className="px-4 py-2 font-bold">
                          Total Monthly Digital Services Cost
                        </td>
                        <td className="px-4 py-2 text-right font-bold">
                          £
                          {digitalServices.totalNewDigitalCost?.toFixed(2) ??
                            "0.00"}{" "}
                          + VAT
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* BILLING */}
                <div className="space-y-2 text-xs text-gray-700">
                  <h3 className="font-bold text-gray-900 uppercase tracking-wide">
                    Billing
                  </h3>
                  <p>
                    All digital service charges will be billed by Fibre Telecoms
                    as part of the Customer's monthly telecom service invoice.
                  </p>
                  <p>
                    Fibre Telecoms will remit the relevant service licence fees
                    to Talk Nerdy To Me Consultancy Limited.
                  </p>
                </div>

                {/* TERM */}
                <div className="space-y-2 text-xs text-gray-700">
                  <h3 className="font-bold text-gray-900 uppercase tracking-wide">
                    Term
                  </h3>
                  <p>
                    The term of the digital services will align with the
                    Customer's telecom agreement with Fibre Telecoms unless
                    otherwise agreed.
                  </p>
                  <p>
                    Services will remain active while the Customer continues to
                    pay the applicable monthly service charges.
                  </p>
                </div>

                {/* DELIVERY AND ONBOARDING */}
                <div className="space-y-2 text-xs text-gray-700">
                  <h3 className="font-bold text-gray-900 uppercase tracking-wide">
                    Delivery and Onboarding
                  </h3>
                  <p>Following acceptance of this agreement:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>
                      Talk Nerdy To Me will contact the Customer to begin
                      service onboarding.
                    </li>
                    <li>
                      The Customer agrees to provide any information required to
                      activate the service.
                    </li>
                    <li>
                      TNTM will configure and deliver the relevant platform
                      services.
                    </li>
                  </ul>
                </div>

                {/* INTELLECTUAL PROPERTY */}
                <div className="space-y-2 text-xs text-gray-700">
                  <h3 className="font-bold text-gray-900 uppercase tracking-wide">
                    Intellectual Property
                  </h3>
                  <p>
                    All platforms, systems, software and digital infrastructure
                    used to deliver these services remain the exclusive
                    intellectual property of Talk Nerdy To Me Consultancy
                    Limited.
                  </p>
                  <p>
                    The Customer receives a licence to use the relevant platform
                    services while the subscription remains active.
                  </p>
                </div>

                {/* TERMS AND CONDITIONS */}
                <div className="space-y-2 text-xs text-gray-700">
                  <h3 className="font-bold text-gray-900 uppercase tracking-wide">
                    Terms and Conditions
                  </h3>
                  <p>
                    These services operate in accordance with the Talk Nerdy To
                    Me Consultancy Limited General Contract Clauses which form
                    part of this agreement.
                  </p>
                </div>

                {/* ACCEPTANCE / Signature */}
                <div className="border p-4 rounded bg-gray-50 mt-4">
                  <h2 className="text-base font-semibold mb-2 uppercase tracking-wide">
                    Acceptance
                  </h2>
                  <p className="text-xs mb-4">
                    By signing this Digital Services Addendum, the Customer
                    confirms acceptance of the selected services and associated
                    monthly charges.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Customer Name
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-sm">
                        {formData.managerName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Position
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-sm">
                        {formData.position}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Authorised Signatory
                      </label>
                      <div className="w-full h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-500 bg-white text-xs">
                        {"{{Sig1_es_:signer1:signature}}"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Date
                      </label>
                      <div className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-sm">
                        {formData.date ? formatToDDMMYYYY(formData.date) : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Received 
        <div className="border p-4 rounded">
          <h2 className="text-lg font-semibold mb-4">DOCUMENTS RECEIVED</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.warrantyAgreement}
                  readOnly
                  className="mr-2"
                />
                WARRANTY AGREEMENT
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.quotationTariff}
                  readOnly
                  className="mr-2"
                />
                QUOTATION - Inc. tariff
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.networkService}
                  readOnly
                  className="mr-2"
                />
                NETWORK SERVICE AGREEMENT
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.rentalDocument}
                  readOnly
                  className="mr-2"
                />
                RENTAL DOCUMENT
              </label>
            </div>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.lineRental}
                  readOnly
                  className="mr-2"
                />
                LINE RENTAL AGREEMENT
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.thisForm}
                  readOnly
                  className="mr-2"
                />
                THIS FORM
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.mobileAirtime}
                  readOnly
                  className="mr-2"
                />
                MOBILE AIRTIME AGREEMENT
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.orderForm}
                  readOnly
                  className="mr-2"
                />
                ORDER FORM
              </label>
            </div>
          </div>
        </div>*/}
        </div>
      </div>
    </div>
  );
});

export default FibreContractForm;
